<template>
  <section id="cart">
    <div id="breadcrumb">
      <div class="container">
        <ul>
          <li><router-link to="/" tag="a">Home</router-link></li>
          <li>
            <router-link to="/rfq/create" tag="a">Create New Quote</router-link>
          </li>
          <li>
            <router-link to="/rfq/shopwearparts" tag="a"
              >Choose Assembly</router-link
            >
          </li>
          <li class="last">Save Quote</li>
        </ul>
      </div>
    </div>
    <!-- The legacy <div id="shoppingPO"> block (with the
         "Shopping with Quote Reference: <num> | Edit Quote
         Reference" affordance) lived here. It's been removed
         because:

         1. The Quote # is already shown prominently in each
            quote card's red header below ("Quote #1", "Quote #2",
            …), and the per-card subtotal pill includes the
            quote reference number, so this block was redundant.

         2. #shoppingPO carries a dark legacy background from
            the original site CSS (and is intentionally preserved
            in steele-consistency-fix.css's "purposefully colored
            sections" list — section 12). Now that #globalBreadcrumbs
            is position:fixed with a transparent background, the
            dark #shoppingPO sat directly behind the breadcrumb
            row at page top and bled through as an edge-to-edge
            gray strip overlapping the breadcrumb text.

         The poNumber/editPoNumber data + changePoNumber method
         are still defined in the script block below — left intact
         in case any saved-quote API call still relies on them
         being present, even though the UI surface is gone. -->

    <div id="cartTop">
      <div class="container">
        <div class="row">
          <div class="alert alert-danger alert-dismissible" v-if="cartError">
            <button
              type="button"
              class="close"
              data-dismiss="alert"
              @click="cartError = false"
            >
              &times;
            </button>
            <strong>Warning!</strong> Please add parts to your cart to continue
            the order process.
          </div>
        </div>

        <!-- ===== PAGE HEADER =====
             "Quoted Items" title with three stacked actions on
             the right: Back to Search (primary navigation, red
             filled), Save Quote (neutral white with dark border),
             and Clear Quotes (destructive white with red border).
             Layout mirrors the cart page's Review & Checkout
             header so users have the same visual pattern across
             both flows. The action group hides when there are no
             quotes — consistent with the cart-page guard. -->
        <div class="rfq-page-header">
          <h1>Quotes Pending Completion</h1>
          <div class="rfq-header-actions" v-if="orders.length > 0">
            <a @click="removePoNumber()"
               class="btn btn-create-new"
               title="Start a fresh quote — clears the current PO context">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="btn-icon">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Create New Quote
            </a>
            <a @click="openModifyModal()"
               class="btn btn-back-search"
               title="Modify a quote's PO number / instructions, or convert it to an order">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="btn-icon">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              Modify / Convert Quote
            </a>
            <a @click="openQuotePicker('saveForLater')"
               :disabled="savingQuote"
               class="btn btn-save-cart"
               title="Save selected quotes to resume later — accessible from Saved Carts">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="btn-icon">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              Save for Later
            </a>
            <a @click="openQuotePicker('finalize')"
               class="btn btn-finalize-quote"
               title="Finalize and print selected quotes">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="btn-icon">
                <polyline points="6 9 6 2 18 2 18 9"></polyline>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                <rect x="6" y="14" width="12" height="8"></rect>
              </svg>
              Finalize / Print Quote
            </a>
            <a @click="clearQuotes()"
               class="btn btn-clear-cart"
               title="Clear quotes from this cart">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="btn-icon">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"></path>
                <path d="M10 11v6"></path>
                <path d="M14 11v6"></path>
                <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
              </svg>
              Clear Quotes
            </a>
          </div>
        </div>
        <!-- #cartTop > .container intentionally left OPEN here; the quote body
             (grid + totals b-form) now renders INSIDE this card, below the
             buttons. The container + #cartTop are closed after </b-form>. -->

    <b-form id="assemblyForm" @submit.prevent="">
      <!-- ===== QUOTES IN 2-COLUMN GRID =====
           Wrapped in a Bootstrap row so two quotes render
           side-by-side on desktop, matching the cart page's
           Order #1 / Order #2 layout (image reference). Stacks
           to a single column below lg where col-lg-6 would be
           too narrow.

           This was briefly switched to col-12 (full-width
           stacked) when the expanded view used a b-table that
           overflowed the col-lg-6 width. The b-table has since
           been replaced with flex-based part-detail rows (see
           steele-consistency-fix.css §24) that wrap gracefully
           in narrow containers, so the 2-column grid is back. -->
      <div class="rfq-orders-grid-wrap"><!-- .container dropped: nested in #cartTop's container -->
        <div class="row align-items-start rfq-orders-grid">
          <!-- Iterate quoteGroupedOrders so each Quote Reference
               renders as ONE Quote # card containing every
               backing PurchaseOrder for that reference. The
               staging area on /rfq/create groups identically
               (Quote #1 PO 100 · 2 parts), so this matches
               that visual grouping pattern.

               Inside each group we still iterate the backing
               orders the same way the original template did —
               every assembly card (machine + parts) is
               preserved verbatim, just nested under one
               grouped header. -->
          <div class="col-lg-6 rfq-order-col"
               v-for="(qgroup, qgIdx) in quoteGroupedOrders"
               :key="'qgroup-' + qgIdx + '-' + (qgroup.number || 'ref')">

            <!-- ===== QUOTE GROUP =====
                 Mirrors the cart's .po-group / .order-header /
                 .assembly-card structure so the two pages read
                 visually consistent. Quote-specific class names
                 keep the styles independent — this page can
                 evolve without dragging the cart along. -->
            <div class="quote-group">

              <!-- Header — Purchase Order # + part count, summed
                   across every backing PurchaseOrder under
                   this Quote Reference. -->
              <div class="quote-header">
                <span class="quote-header-label">
                  Purchase Order #{{ qgroup.number }}
                </span>
                <span class="quote-header-meta">
                  {{ qgroup.totalParts }}
                  {{ qgroup.totalParts === 1 ? 'part' : 'parts' }}
                </span>
              </div>

              <!-- Render each backing PurchaseOrder under this
                   quote reference as its own assembly card.
                   Single-assembly Quote References just show one
                   card; multi-assembly references show one card
                   per machine+assembly combo, all stacked under
                   the same Quote # header above. -->
              <template v-for="(order, qIdx) in qgroup.orders">
              <div class="quote-assembly-card-wrap"
                   :key="'qord-' + order.id">

              <!-- Inner assembly card. Holds the machine title +
                   assembly name on the left, subtotal pill on
                   the right (or stacked below in narrow cols). -->
              <div class="quote-assembly-card">
                <div class="quote-assembly-header">
                  <div class="quote-assembly-heading">
                    <div class="assemblyName">
                      {{ order.assembly.machine.description }}
                    </div>
                    <div class="assemblyDescription">
                      {{ order.assembly.name }}
                    </div>
                  </div>

                  <!-- Subtotal pill — same gray-card treatment
                       as the cart's PO subtotal so the two
                       pages match visually. -->
                  <div class="quote-assembly-subtotal">
                    <span class="quote-subtotal-label">
                      Subtotal for Quote Reference:
                      <strong>{{ order.number }}</strong>
                    </span>
                    <span class="quote-subtotal-amount">
                      <template v-if="order.cost > 0">
                        {{ order.cost | currency }}
                      </template>
                      <template v-else>$0.00</template>
                    </span>
                  </div>
                </div>

                <!-- ===== COMPACT PARTS SUMMARY (default view) =====
                     One row per part as a pill. Clicking the
                     expand button toggles the full editable
                     part-detail rows below for users who need
                     to update quantities or remove parts. -->
                <div class="quote-parts-summary"
                     v-show="!expandedQuotes[order.id]">
                  <button type="button"
                          class="quote-expand-btn"
                          :title="'Expand quote #' + (qIdx + 1) + ' to edit parts'"
                          @click="toggleQuoteExpand(order.id)">
                    <svg xmlns="http://www.w3.org/2000/svg"
                         width="14" height="14" viewBox="0 0 24 24"
                         fill="none" stroke="currentColor"
                         stroke-width="2.5"
                         stroke-linecap="round" stroke-linejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </button>
                  <span class="quote-parts-count">
                    {{ order.parts.length }}
                    {{ order.parts.length === 1 ? 'PART' : 'PARTS' }}
                  </span>
                  <div class="quote-parts-pills">
                    <span class="quote-part-pill"
                          v-for="(p, pIdx) in order.parts"
                          :key="'ppill-' + order.id + '-' + pIdx">
                      {{ p.part.code }} ×{{ p.quantity }}
                    </span>
                  </div>
                  <span class="quote-parts-cost"
                        v-if="order.cost > 0">
                    {{ order.cost | currency }}
                  </span>
                </div>

                <!-- ===== EXPANDED PARTS DETAIL (editable) =====
                     Modern flex-based detail rows replacing the
                     old b-table — see steele-consistency-fix.css
                     §24 for the styling. Each part renders as a
                     self-contained row that wraps gracefully on
                     narrow widths instead of horizontal-scrolling
                     out of the card. -->
                <div class="quote-parts-detail"
                     v-show="expandedQuotes[order.id]">
                  <button type="button"
                          class="quote-collapse-btn"
                          @click="toggleQuoteExpand(order.id)">
                    <svg xmlns="http://www.w3.org/2000/svg"
                         width="12" height="12" viewBox="0 0 24 24"
                         fill="none" stroke="currentColor"
                         stroke-width="2.5"
                         stroke-linecap="round" stroke-linejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Collapse
                  </button>

                  <div class="parts-detail-list">
                    <div class="part-detail-row"
                         v-for="(p, pIdx) in order.parts"
                         :key="'rfq-pdrow-' + order.id + '-' + pIdx">
                      <div class="part-detail-head">
                        <div class="part-detail-meta">
                          <span class="part-item-num">{{ p.part.item }}</span>
                          <span class="part-detail-divider">·</span>
                          <span class="part-code">{{ p.part.code }}</span>
                          <!-- 2% Discount chip — green pill rendered
                               inline next to the part code on the
                               main-page assembly-expanded part rows.
                               Same isWearEligible(p) helper used by
                               the Modify modal chip earlier in this
                               file. Parent .part-detail-meta is a
                               flex ROW, so no align-self workaround
                               is needed (chips size to content
                               naturally with display: inline-block
                               and white-space: nowrap). -->
                          <span class="part-discount-chip"
                                v-if="isWearEligible(p)">
                            2% Discount
                          </span>
                          <span class="part-detail-divider"
                                v-if="p.part.description">·</span>
                          <span class="part-desc"
                                v-if="p.part.description">{{ p.part.description }}</span>
                        </div>
                        <a class="part-detail-remove"
                           title="Remove this part"
                           @click="removeItem(p, order); updateSubtotal(order)">×</a>
                      </div>
                      <div class="part-detail-body">
                        <div class="part-stat">
                          <div class="part-stat-label">Qty / Machine</div>
                          <div class="part-stat-value">
                            {{ p.part.quantityPerMachine }}
                          </div>
                        </div>
                        <div class="part-stat">
                          <div class="part-stat-label">Price</div>
                          <div class="part-stat-value">
                            {{ p.part.price | currency }}
                          </div>
                        </div>
                        <div class="part-stat part-stat-qty">
                          <div class="part-stat-label">Cart Qty</div>
                          <div class="part-qty-controls">
                            <input type="text"
                                   v-model="p.quantity"
                                   class="part-qty-input" />
                            <button type="button"
                                    class="part-qty-update btn btn-dark"
                                    @click="
                                      if (p.quantity >= 1) {
                                        updateSubtotal(order);
                                      } else if (p.quantity < 1) {
                                        removeItem(p, order);
                                      }
                                    ">
                              Update
                            </button>
                          </div>
                        </div>
                        <div class="part-stat part-stat-cost">
                          <div class="part-stat-label">Cost</div>
                          <div class="part-stat-value part-cost-amount">
                            {{ (p.part.price * p.quantity) | currency }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Per-quote Special Instructions — read-only
                       display below the parts list. Only renders
                       when the user has actually written
                       instructions for this quote (no empty box
                       when unset). Edits happen via the Modify /
                       Convert Quote modal in the page header. -->
                  <div class="quote-special-instructions"
                       v-if="order.specialInstructions">
                    <div class="quote-special-instructions-label">
                      Special Instructions
                    </div>
                    <div class="quote-special-instructions-text">
                      {{ order.specialInstructions }}
                    </div>
                  </div>
                </div>
              </div>

              </div><!-- /.quote-assembly-card-wrap -->
              </template><!-- /v-for backing orders within this quote group -->
            </div>

          </div><!-- /.col-lg-6 rfq-order-col -->
        </div><!-- /.row rfq-orders-grid -->
      </div><!-- /.container rfq-orders-grid-wrap -->

      <!-- ===== BOTTOM SECTION =====
           Just the quote-total bar and a leadtime note now.
           The old Special Instructions textarea and 3-button
           action row (Shop with New Quote / Save Quote /
           Convert to Order) are gone — those concerns moved
           up to the top header buttons (Save for Later,
           Finalize / Print Quote, Modify / Convert Quote)
           and into the Modify / Convert modal where each
           quote can have its own instructions. -->
      <div id="bottomTotals" class="mt-4">
        <div><!-- .container dropped: nested in #cartTop's container -->

          <!-- Quote total bar — single centered line. -->
          <div class="rfq-total-bar">
            <span>Quote Total:</span>
            <span class="rfq-total-bar-amount">{{ total | currency }}</span>
          </div>

          <p class="rfq-leadtime-note">
            All web orders have a standard 2 week lead time, but may be
            shipped sooner.
          </p>
        </div>
      </div>
    </b-form>
      </div><!-- /.container (#cartTop) — wraps the quote body now -->
    </div><!-- /#cartTop -->

    <!-- ===== QUOTE PICKER MODAL =====
         Shared by both "Save for Later" and "Finalize /
         Print Quote". Lists each quote with a checkbox so
         the user can pick which subset the action applies
         to. pendingPickerAction tracks which button opened
         the modal; confirmQuotePicker dispatches accordingly.
         Title and confirm button label adapt based on the
         pending action. -->
    <b-modal id="quote-picker-modal"
             v-model="showQuotePickerModal"
             :title="quotePickerTitle"
             size="md"
             centered
             modal-class="quote-picker-modal"
             :ok-title="quotePickerOkTitle"
             ok-variant="primary"
             cancel-title="Cancel"
             cancel-variant="outline-secondary"
             :ok-disabled="selectedQuotesForPicker.length === 0"
             @ok="confirmQuotePicker">

      <!-- ===== CONTEXT BANNER =====
           Same visual treatment as the Modify Quote modal's
           banner so the two flows feel consistent. Title +
           body vary by pendingPickerAction (saveForLater /
           finalize). Hidden when banner is null. -->
      <div v-if="quotePickerBanner"
           class="modify-restored-banner quote-picker-banner"
           :class="'quote-picker-banner-' + pendingPickerAction">
        <div class="modify-restored-title">
          {{ quotePickerBanner.title }}
        </div>
        <div class="modify-restored-body" v-html="quotePickerBanner.body"></div>
      </div>

      <p class="convert-modal-intro">
        {{ quotePickerIntro }}
      </p>

      <div class="convert-quote-list">
        <!-- Group-level rows. One checkbox per Quote # group;
             checking it adds all backing order ids in the
             group to selectedQuotesForPicker. Group label
             reads "Quote #N · Quote Ref · N assemblies ·
             total cost" with the constituent assembly names
             listed below for context. Mirrors the on-page
             grouped rendering. -->
        <div v-for="(qg, qgIdx) in quoteGroupedOrders"
             :key="'pick-grp-' + qgIdx"
             class="convert-quote-row convert-quote-row-grouped">
          <b-form-checkbox
            :checked="isQuoteGroupFullySelected(qg, selectedQuotesForPicker)"
            :indeterminate="isQuoteGroupPartiallySelected(qg, selectedQuotesForPicker)"
            @change="setQuoteGroupSelection(qg, $event, 'selectedQuotesForPicker')">
            <div class="convert-quote-row-main">
              <strong class="convert-quote-row-label">
                Purchase Order #{{ qg.number }}
              </strong>
            </div>
            <div class="convert-quote-row-sub">
              {{ qg.orders.length }}
              {{ qg.orders.length === 1 ? 'item' : 'items' }} ·
              {{ qg.totalParts }}
              {{ qg.totalParts === 1 ? 'part' : 'parts' }}
              <span class="convert-quote-row-amount"
                    v-if="qg.totalCost > 0">
                · {{ qg.totalCost | currency }}
              </span>
            </div>
            <div class="convert-quote-row-assemblies"
                 v-if="qg.orders.length > 0">
              <div v-for="o in qg.orders"
                   :key="'pick-asm-' + qgIdx + '-' + o.id"
                   class="convert-quote-row-assembly">
                <span v-if="o.assembly && o.assembly.machine && o.assembly.machine.description">
                  {{ o.assembly.machine.description }}
                </span>
                <template v-if="o.assembly && o.assembly.name">
                  · {{ o.assembly.name }}
                </template>
                <template v-if="o.parts && o.parts.length">
                  · {{ o.parts.length }} {{ o.parts.length === 1 ? 'part' : 'parts' }}
                </template>
              </div>
            </div>
          </b-form-checkbox>
        </div>
      </div>

      <div class="convert-quote-toggle-all">
        <a href="#"
           @click.prevent="toggleSelectAllQuotesForPicker">
          {{ selectedQuotesForPicker.length === orders.length
              ? 'Deselect All'
              : 'Select All' }}
        </a>
      </div>

      <p v-if="pendingPickerAction === 'saveForLater'
               && selectedQuotesForPicker.length > 0
               && selectedQuotesForPicker.length < orders.length"
         class="convert-quote-note">
        The selected
        {{ countSelectedQuoteGroups(selectedQuotesForPicker) === 1 ? 'quote' : 'quotes' }}
        will be saved to your Saved Carts. The unselected
        {{ quoteGroupedOrders.length - countSelectedQuoteGroups(selectedQuotesForPicker) === 1
            ? 'quote stays'
            : 'quotes stay' }}
        in your active cart so you can keep editing.
      </p>
      <p v-if="pendingPickerAction === 'finalize'
               && selectedQuotesForPicker.length > 0
               && selectedQuotesForPicker.length < orders.length"
         class="convert-quote-note">
        Only the selected
        {{ countSelectedQuoteGroups(selectedQuotesForPicker) === 1 ? 'quote' : 'quotes' }}
        will appear on the printed quote. The other
        {{ quoteGroupedOrders.length - countSelectedQuoteGroups(selectedQuotesForPicker) === 1
            ? 'quote will remain'
            : 'quotes will remain' }}
        in your active cart.
      </p>
      <p v-if="selectedQuotesForPicker.length === 0"
         class="convert-quote-note convert-quote-note-warn">
        Select at least one quote to continue.
      </p>
    </b-modal>

    <!-- ===== CONVERT-TO-ORDER MODAL =====
         Triggered by the "Convert to Order" button in the bottom
         action row. Lists each saved quote with a checkbox so the
         user can pick which ones go into the new active cart.
         Defaults to all selected (most common case is "convert
         everything"). The confirm handler decides whether to use
         the cheap full-conversion path (discriminator flip in
         place) or the partial-conversion path (new cart + re-parent
         selected POs) based on whether all checkboxes remain
         checked. See createOrder() / confirmConvertToOrder() in
         the methods block. -->
    <b-modal id="convert-to-order-modal"
             v-model="showConvertModal"
             title="Convert Quotes to Order"
             size="md"
             centered
             modal-class="quote-picker-modal"
             ok-title="Convert Selected to Order"
             ok-variant="primary"
             cancel-title="Cancel"
             cancel-variant="outline-secondary"
             :ok-disabled="selectedQuotesForConversion.length === 0"
             @ok="confirmConvertToOrder">

      <!-- ===== CONTEXT BANNER =====
           Same visual treatment as the Modify Quote modal's
           banner so all quote-action modals feel consistent. -->
      <div class="modify-restored-banner">
        <div class="modify-restored-title">
          Convert Quote to Order
        </div>
        <div class="modify-restored-body">
          Pick which quotes to roll forward into a real order.
          The unselected ones stay as saved quotes for now.
        </div>
      </div>

      <p class="convert-modal-intro">
        Select which quotes you want to include in your new order:
      </p>

      <div class="convert-quote-list">
        <div v-for="(qg, qgIdx) in quoteGroupedOrders"
             :key="'cnv-grp-' + qgIdx"
             class="convert-quote-row convert-quote-row-grouped">
          <b-form-checkbox
            :checked="isQuoteGroupFullySelected(qg, selectedQuotesForConversion)"
            :indeterminate="isQuoteGroupPartiallySelected(qg, selectedQuotesForConversion)"
            @change="setQuoteGroupSelection(qg, $event, 'selectedQuotesForConversion')">
            <div class="convert-quote-row-main">
              <strong class="convert-quote-row-label">
                Purchase Order #{{ qg.number }}
              </strong>
            </div>
            <div class="convert-quote-row-sub">
              {{ qg.orders.length }}
              {{ qg.orders.length === 1 ? 'item' : 'items' }} ·
              {{ qg.totalParts }}
              {{ qg.totalParts === 1 ? 'part' : 'parts' }}
              <span class="convert-quote-row-amount"
                    v-if="qg.totalCost > 0">
                · {{ qg.totalCost | currency }}
              </span>
            </div>
            <div class="convert-quote-row-assemblies"
                 v-if="qg.orders.length > 0">
              <div v-for="o in qg.orders"
                   :key="'cnv-asm-' + qgIdx + '-' + o.id"
                   class="convert-quote-row-assembly">
                <span v-if="o.assembly && o.assembly.machine && o.assembly.machine.description">
                  {{ o.assembly.machine.description }}
                </span>
                <template v-if="o.assembly && o.assembly.name">
                  · {{ o.assembly.name }}
                </template>
                <template v-if="o.parts && o.parts.length">
                  · {{ o.parts.length }} {{ o.parts.length === 1 ? 'part' : 'parts' }}
                </template>
              </div>
            </div>
          </b-form-checkbox>
        </div>
      </div>

      <div class="convert-quote-toggle-all">
        <a href="#"
           @click.prevent="toggleSelectAllQuotesForConversion">
          {{ selectedQuotesForConversion.length === orders.length
              ? 'Deselect All'
              : 'Select All' }}
        </a>
      </div>

      <p v-if="selectedQuotesForConversion.length > 0
               && selectedQuotesForConversion.length < orders.length"
         class="convert-quote-note">
        The {{ quoteGroupedOrders.length - countSelectedQuoteGroups(selectedQuotesForConversion) }}
        unselected
        {{ quoteGroupedOrders.length - countSelectedQuoteGroups(selectedQuotesForConversion) === 1
            ? 'quote'
            : 'quotes' }}
        will remain as saved
        {{ quoteGroupedOrders.length - countSelectedQuoteGroups(selectedQuotesForConversion) === 1
            ? 'quote'
            : 'quotes' }}.
      </p>
      <p v-if="selectedQuotesForConversion.length === 0"
         class="convert-quote-note convert-quote-note-warn">
        Select at least one quote to convert.
      </p>
    </b-modal>

    <!-- ===== CLEAR QUOTES MODAL =====
         Triggered by the "Clear Quotes" header button when there
         are 2+ quotes. Lists each saved quote with a checkbox so
         the user can pick which ones to delete. Defaults to NONE
         selected — destructive default behaviour should require
         explicit opt-in for each quote, unlike the convert modal
         where "convert all" is the safe common case.

         When orders.length === 1 the page skips this modal and
         shows a simple msgBoxConfirm instead — see clearQuotes()
         in the methods block. The branching keeps the UX light
         for the common single-quote case but offers granular
         control when the user has built up multiple quotes. -->
    <b-modal id="clear-quotes-modal"
             v-model="showClearQuotesModal"
             title="Clear Quotes"
             size="md"
             centered
             modal-class="quote-picker-modal clear-quotes-modal"
             :ok-title="clearQuotesOkTitle"
             ok-variant="danger"
             cancel-title="Cancel"
             cancel-variant="outline-secondary"
             :ok-disabled="selectedQuotesForClear.length === 0"
             @ok="confirmClearQuotes">

      <!-- ===== CONTEXT BANNER =====
           Destructive variant — uses the same banner shell
           but with a warning tone since the action removes
           quotes from the cart permanently. -->
      <div class="modify-restored-banner clear-banner">
        <div class="modify-restored-title">
          Delete Quote(s)
        </div>
        <div class="modify-restored-body">
          Pick which quotes to permanently delete from your
          active cart. <strong>This cannot be undone.</strong>
        </div>
      </div>

      <p class="convert-modal-intro">
        Select which quotes you want to delete. This cannot be undone.
      </p>

      <div class="convert-quote-list">
        <div v-for="(qg, qgIdx) in quoteGroupedOrders"
             :key="'clr-grp-' + qgIdx"
             class="convert-quote-row convert-quote-row-grouped">
          <b-form-checkbox
            :checked="isQuoteGroupFullySelected(qg, selectedQuotesForClear)"
            :indeterminate="isQuoteGroupPartiallySelected(qg, selectedQuotesForClear)"
            @change="setQuoteGroupSelection(qg, $event, 'selectedQuotesForClear')">
            <div class="convert-quote-row-main">
              <strong class="convert-quote-row-label">
                Purchase Order #{{ qg.number }}
              </strong>
            </div>
            <div class="convert-quote-row-sub">
              {{ qg.orders.length }}
              {{ qg.orders.length === 1 ? 'item' : 'items' }} ·
              {{ qg.totalParts }}
              {{ qg.totalParts === 1 ? 'part' : 'parts' }}
              <span class="convert-quote-row-amount"
                    v-if="qg.totalCost > 0">
                · {{ qg.totalCost | currency }}
              </span>
            </div>
            <div class="convert-quote-row-assemblies"
                 v-if="qg.orders.length > 0">
              <div v-for="o in qg.orders"
                   :key="'clr-asm-' + qgIdx + '-' + o.id"
                   class="convert-quote-row-assembly">
                <span v-if="o.assembly && o.assembly.machine && o.assembly.machine.description">
                  {{ o.assembly.machine.description }}
                </span>
                <template v-if="o.assembly && o.assembly.name">
                  · {{ o.assembly.name }}
                </template>
                <template v-if="o.parts && o.parts.length">
                  · {{ o.parts.length }} {{ o.parts.length === 1 ? 'part' : 'parts' }}
                </template>
              </div>
            </div>
          </b-form-checkbox>
        </div>
      </div>

      <div class="convert-quote-toggle-all">
        <a href="#"
           @click.prevent="toggleSelectAllQuotesForClear">
          {{ selectedQuotesForClear.length === orders.length
              ? 'Deselect All'
              : 'Select All' }}
        </a>
      </div>

      <p v-if="selectedQuotesForClear.length > 0
               && selectedQuotesForClear.length < orders.length"
         class="convert-quote-note">
        The {{ quoteGroupedOrders.length - countSelectedQuoteGroups(selectedQuotesForClear) }}
        unselected
        {{ quoteGroupedOrders.length - countSelectedQuoteGroups(selectedQuotesForClear) === 1
            ? 'quote'
            : 'quotes' }}
        will be kept.
      </p>
      <p v-if="selectedQuotesForClear.length === orders.length"
         class="convert-quote-note convert-quote-note-warn">
        All quotes will be deleted and the cart will be cleared.
      </p>
      <p v-if="selectedQuotesForClear.length === 0"
         class="convert-quote-note">
        Select at least one quote to delete.
      </p>
    </b-modal>

    <!-- ===== MODIFY / CONVERT QUOTE MODAL =====
         The user can pick any quote in their cart and:
         · Edit its PO Number (Quote Reference)
         · Edit its Special Instructions
         · Optionally tick "Apply instructions to all quotes"
           so they don't have to re-type the same note for
           every quote
         · Save those changes (persists to server-side cart)
         · Add more parts to that quote (pre-fills the
           create page — same flow as before)
         · Convert that quote (or all quotes) to an order

         Replaces the old Special Instructions textarea
         that lived at the bottom of the page. -->
    <b-modal
      v-model="showModifyModal"
      title="Modify / Convert Quote"
      size="lg"
      hide-footer
      modal-class="modify-quote-modal"
      @show="onModifyModalOpen"
      @hidden="onModifyModalClose"
    >
      <div v-if="modifyOptions.length === 0" class="modify-modal-intro">
        Nothing to modify — your quote cart appears to be empty.
      </div>
      <div v-else>
        <!-- ===== CONTEXT BANNER =====
             High-emphasis confirmation panel rendered from
             the modifyBanner computed. Title + body vary
             by modifyContext (restored-quote / modifying-
             quote / saving-quote / placing-quote /
             converting). Boxed, bordered, shadowed so
             the eye is drawn to it on open. Hidden when
             modifyContext is null (legacy callsites that
             open without setting context). -->
        <div v-if="modifyBanner"
             class="modify-restored-banner"
             :class="'modify-banner-' + modifyContext">
          <div class="modify-restored-title">
            {{ modifyBanner.title }}
          </div>
          <div class="modify-restored-body" v-html="modifyBanner.body"></div>
        </div>

        <!-- Quote picker — radio rows so it's clear only
             one quote is being edited at a time. -->
        <p class="modify-modal-intro">
          Pick a quote to edit its PO Number or Special Instructions,
          add more parts, or convert it to an order.
        </p>
        <div class="modify-quote-picker" data-rfq-picker="grouped-v2">
          <!-- Hierarchical picker:
                 1. Group header (Quote #N · Quote Ref · ITEM count)
                    with chevron — clicking expands.
                 2. Expanded view shows each assembly row with a
                    radio button on the left.
                 3. Each assembly row, when expanded, also lists
                    its parts with code · description · qty · cost
                    and an × button to remove that part.
               All groups start collapsed (toggleModifyGroup +
               expandedModifyGroups state) so the user can see
               every Quote # at a glance, then drill in. Same
               pattern as Cart.vue's Modify Order modal. -->
          <div v-for="(qg, qgIdx) in quoteGroupedOrders"
               :key="'mqg-' + qgIdx"
               class="modify-order-group"
               :class="{
                 'is-multi': qg.orders.length > 1,
                 'is-expanded': expandedModifyGroups[qgIdx]
               }">

            <!-- Top row — clickable group header. The whole
                 row is the chevron toggle so users can click
                 anywhere on the bar to expand. -->
            <div class="modify-order-group-head-row">
              <button type="button"
                      class="modify-order-group-head"
                      @click="toggleModifyGroup(qgIdx)">
                <span class="modify-order-group-label">
                  Purchase Order #{{ qg.number }}
                </span>
                <span class="modify-order-group-total"
                      v-if="qg.totalCost > 0">
                  {{ qg.totalCost | currency }}
                </span>
                <span class="modify-order-group-count">
                  {{ qg.orders.length }}
                  {{ qg.orders.length === 1 ? 'ITEM' : 'ITEMS' }}
                </span>
              </button>
            </div>

            <!-- Chevron + helper text below the title bar.
                 Visually subordinate so the header reads as
                 the primary identifier. Helper text wording
                 flips between expanded/collapsed states so
                 users always know what the click will do. -->
            <button type="button"
                    class="modify-order-group-toggle"
                    :class="{ 'is-open': expandedModifyGroups[qgIdx] }"
                    @click="toggleModifyGroup(qgIdx)">
              <span class="modify-order-group-toggle-icon">
                {{ expandedModifyGroups[qgIdx] ? '−' : '+' }}
              </span>
              <span class="modify-order-group-toggle-text">
                {{ expandedModifyGroups[qgIdx]
                    ? 'Hide item details'
                    : 'Click the plus to see item details' }}
              </span>
            </button>

            <!-- Assembly rows — visible only when expanded.
                 Radio sets activeModifyId which reveals the
                 PO/Special Instructions editor below. Parts
                 list inside each row has individual delete
                 X buttons. -->
            <div v-if="expandedModifyGroups[qgIdx]"
                 class="modify-order-group-assemblies">
              <div v-for="order in qg.orders"
                   :key="'pick-wrap-' + order.id"
                   class="modify-quote-pick-wrap">
                <label
                  class="modify-quote-pick-row"
                  :class="{ 'is-selected': activeModifyId === order.id }">
                  <input type="radio"
                         :value="order.id"
                         v-model="activeModifyId"
                         class="modify-quote-pick-radio" />
                  <div class="modify-quote-pick-body">
                    <div class="modify-option-head">
                      <span class="modify-option-assembly">
                        {{ (order.assembly && order.assembly.name) || '—' }}
                      </span>
                    </div>
                    <div class="modify-option-meta">
                      <span v-if="order.assembly && order.assembly.machine && order.assembly.machine.description">
                        {{ order.assembly.machine.description }}
                      </span>
                      <span v-if="order.assembly && order.assembly.machine && order.assembly.machine.description">·</span>
                      <span>
                        {{ (order.parts || []).length }}
                        {{ (order.parts || []).length === 1 ? 'part' : 'parts' }}
                      </span>
                      <span v-if="order.specialInstructions"
                            class="modify-option-instr-flag">
                        · has instructions
                      </span>
                    </div>
                  </div>
                  <span class="modify-option-cost"
                        v-if="order.cost > 0">
                    {{ order.cost | currency }}
                  </span>
                </label>

                <!-- Parts list — code · description · qty ·
                     cost · × delete. .stop.prevent on the
                     delete click so it doesn't bubble up
                     into the radio's label and accidentally
                     select that assembly. -->
                <div class="modify-parts-list"
                     v-if="order.parts && order.parts.length > 0">
                  <div v-for="p in order.parts"
                       :key="'mpart-' + order.id + '-' + p.id"
                       class="modify-part-row">
                    <div class="modify-part-info">
                      <span class="modify-part-code">
                        {{ p.part && p.part.code ? p.part.code : '—' }}
                      </span>
                      <!-- 2% Discount chip — green pill next to
                           the part code when the part is wear-
                           eligible. Same pattern as Cart.vue's
                           Modify Order modal. wearPartIds is
                           loaded by this component on mount;
                           if the map is empty or the part isn't
                           in it, isWearEligible returns false
                           and the chip simply doesn't render. -->
                      <span class="modify-part-discount-chip"
                            v-if="isWearEligible(p)">
                        2% Discount
                      </span>
                      <span class="modify-part-desc"
                            v-if="p.part && p.part.description">
                        {{ p.part.description }}
                      </span>
                    </div>
                    <span class="modify-part-qty">
                      ×{{ p.quantity || 0 }}
                    </span>
                    <span class="modify-part-cost">
                      {{ ((p.part && p.part.price ? p.part.price : 0) * (p.quantity || 0)) | currency }}
                    </span>
                    <button type="button"
                            class="modify-part-remove"
                            :title="'Remove ' + (p.part && p.part.code ? p.part.code : 'this part')"
                            @click.stop.prevent="removeItem(p, order); updateSubtotal(order)">
                      ×
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Editor — bound to activeModifyId. PO Number
             and Special Instructions are local edits;
             they write through to this.orders on Save. -->
        <div class="modify-editor" v-if="activeModifyOption">
          <div class="modify-editor-row">
            <label class="modify-editor-label" for="modifyPoNumber">
              PO Number / Quote Reference
            </label>
            <input id="modifyPoNumber"
                   type="text"
                   v-model.trim="modifyPoNumberDraft"
                   class="modify-editor-input"
                   placeholder="Enter PO Number" />
          </div>

          <div class="modify-editor-row">
            <label class="modify-editor-label" for="modifySpecialInstructions">
              Special Instructions
            </label>
            <textarea id="modifySpecialInstructions"
                      v-model="modifySpecialInstructionsDraft"
                      class="modify-editor-input modify-editor-textarea"
                      rows="4"
                      placeholder="Notes or instructions for this quote"></textarea>
          </div>

          <div class="modify-editor-row modify-editor-toggle-row"
               v-if="quoteGroupedOrders.length > 1">
            <label class="modify-apply-all">
              <input type="checkbox"
                     v-model="applyInstructionsToAll" />
              <span>
                Apply these Special Instructions to all
                {{ quoteGroupedOrders.length }} quotes
              </span>
            </label>
          </div>
        </div>

        <!-- Action row — three actions, each with a clear
             primary verb. Save Changes is the most common
             so it's the leftmost / default visual weight. -->
        <div class="modify-actions" v-if="activeModifyOption">
          <button type="button"
                  class="btn btn-primary modify-action-btn"
                  :disabled="savingModify"
                  @click="saveModifyChanges">
            {{ savingModify ? 'Saving…' : 'Save Changes' }}
          </button>
          <button type="button"
                  class="btn btn-dark modify-action-btn"
                  :disabled="savingModify"
                  @click="goAddMoreParts">
            Add More Parts
          </button>
          <button type="button"
                  class="btn btn-outline-primary modify-action-btn"
                  :disabled="savingModify || convertingFromModify"
                  @click="convertFromModify">
            {{ convertingFromModify ? 'Converting…' : 'Convert to Order' }}
          </button>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import FeApiService from "@/assets/js/FeApiService";
import { cleanupTempSetupAdds } from "@/assets/js/tempSetupAdds";
const apiService = new FeApiService();

import "expose-loader?$!expose-loader?jQuery!jquery";
import "semantic-ui-dropdown/dropdown.js";

import Vue from "vue";
import Vue2Filters from "vue2-filters";

Vue.use(Vue2Filters);

export default {
  name: "RFQCart",
  computed: {
    // Map order.id → "Purchase Order #{number}" label using
    // the actual quote reference (qg.number) where available,
    // falling back to a sequential "Quote #N" if for any
    // reason the reference is missing. Used by the quote
    // picker, convert-to-order, and clear-quotes modals so
    // all per-order labels match the on-page "Purchase Order
    // #" header convention.
    quoteLabelByOrderId() {
      var map = {};
      (this.quoteGroupedOrders || []).forEach(function (qg, gIdx) {
        var label = qg.number
          ? "Purchase Order #" + qg.number
          : "Quote #" + (gIdx + 1);
        (qg.orders || []).forEach(function (o) {
          map[o.id] = label;
        });
      });
      return map;
    },

    // ===== QUOTE-GROUPED ORDERS =====
    // Consolidates this.orders by Quote Reference (order.number)
    // so the page renders ONE Quote # per reference, with all
    // its backing PurchaseOrder rows nested inside as separate
    // assembly cards. Without this, the page rendered each
    // backing PurchaseOrder as its own Quote # card —
    // a partial save flow that produces 2 backing rows for
    // the same Quote Reference would show as Quote #1 and
    // Quote #2 instead of one Quote with two assemblies.
    //
    // Returns an array of:
    //   { number: <quoteReference>, orders: [<backing orders>],
    //     totalCost: <sum of order.cost>, totalParts: <sum of part counts> }
    //
    // Orders sort within each group by id so the assembly
    // order is stable across renders (no jumping when a part
    // is added / removed).
    quoteGroupedOrders() {
      var groups = [];
      var byRef = {};
      (this.orders || []).forEach(function (order) {
        var key = order.number != null
          ? String(order.number)
          : "__noref__" + order.id;
        if (!byRef[key]) {
          byRef[key] = {
            number: order.number,
            orders: [],
            totalCost: 0,
            totalParts: 0,
          };
          groups.push(byRef[key]);
        }
        byRef[key].orders.push(order);
        byRef[key].totalCost += Number(order.cost) || 0;
        byRef[key].totalParts += (order.parts || []).length;
      });
      groups.forEach(function (g) {
        g.orders.sort(function (a, b) {
          return (a.id || 0) - (b.id || 0);
        });
      });
      return groups;
    },

    // ===== MODIFY MODAL BANNER COPY =====
    // Maps modifyContext to the title + body shown in the
    // prominent banner at the top of the modal. Returns
    // null when no banner should render. Mirrors Cart.vue's
    // modifyBanner computed but with quote-specific copy.
    modifyBanner() {
      switch (this.modifyContext) {
        case "restored-quote":
          return {
            title: "Quote Restored Successfully",
            body:
              "Click <strong>Go To Quote</strong> below or click any " +
              "quote to make changes to the Quote Reference or add " +
              "Special Instructions.",
          };
        case "modifying-quote":
          return {
            title: "Modify Your Quote",
            body:
              "Pick a quote to edit its Quote Reference, " +
              "Special Instructions, or to add more parts.",
          };
        case "saving-quote":
          return {
            title: "Save Quote(s) for Later",
            body: "Review the quotes below before choosing which to save.",
          };
        case "placing-quote":
          return {
            title: "Ready to Finalize the Quote?",
            body: "Confirm details below.",
          };
        case "converting":
          return {
            title: "Convert Quote to Order",
            body: "Review the quote details, then click Convert to Order.",
          };
        default:
          return null;
      }
    },

    // ===== PRIMARY ACTION LABEL =====
    // Drives the Save Changes ↔ context-specific button
    // label split on the modal's primary button. Returns
    // "Save Changes" when there are unsaved edits;
    // otherwise returns context-specific text.
    modifyPrimaryLabel() {
      if (this.savingModify) return "Saving…";
      if (this.hasModifyChanges) return "Save Changes";
      switch (this.modifyContext) {
        case "placing-quote":
          return "Finalize Quote";
        case "saving-quote":
          return "Continue to Save";
        case "modifying-quote":
          return "Done";
        case "converting":
          return "Convert to Order";
        case "restored-quote":
        default:
          return "Go To Quote";
      }
    },

    // ===== DIRTY-CHECK FOR MODIFY EDITOR =====
    // True when the user has typed something into the PO
    // Number or Special Instructions fields that doesn't
    // match the active row's saved values. Drives the
    // primary button's "Save Changes" face vs the context
    // label.
    hasModifyChanges() {
      var active = this.activeModifyOption;
      if (!active) return false;
      var savedPo = (active.poNumber || "").trim();
      var savedInstr = active.specialInstructions || "";
      var draftPo = (this.modifyPoNumberDraft || "").trim();
      var draftInstr = this.modifySpecialInstructionsDraft || "";
      if (draftPo !== savedPo) return true;
      if (draftInstr !== savedInstr) return true;
      if (this.applyInstructionsToAll) return true;
      return false;
    },

    // OK-button label for the Clear Quotes modal. Dynamic so
    // the user sees the exact count of quotes about to be
    // removed ("Delete 2 Quotes") — beats a generic verb at
    // making the consequences of the click obvious. Disabled
    // state is handled separately via :ok-disabled in the
    // template.
    clearQuotesOkTitle() {
      var n = this.countSelectedQuoteGroups(this.selectedQuotesForClear);
      if (n === 0) return "Delete";
      if (n === 1) return "Delete 1 Quote";
      return "Delete " + n + " Quotes";
    },
    // ===== QUOTE PICKER MODAL TEXT =====
    // The shared picker modal is opened by both "Save for
    // Later" and "Finalize / Print Quote". Title, intro
    // copy, and OK button label all flex based on which
    // button triggered it. Falls back gracefully when the
    // modal is closed (pendingPickerAction is null).
    quotePickerTitle() {
      if (this.pendingPickerAction === "saveForLater") {
        return "Save Selected Quotes for Later";
      }
      if (this.pendingPickerAction === "finalize") {
        return "Finalize / Print Selected Quotes";
      }
      return "Select Quotes";
    },
    quotePickerIntro() {
      if (this.pendingPickerAction === "saveForLater") {
        return "Select which quotes you want to save for later. " +
          "They'll be available from Saved Carts. Any unselected " +
          "quotes stay in your active cart so you can keep editing them.";
      }
      if (this.pendingPickerAction === "finalize") {
        return "Select which quotes you want to include on the " +
          "finalized / printed quote.";
      }
      return "Select quotes to continue.";
    },

    // ===== QUOTE PICKER BANNER COPY =====
    // High-emphasis confirmation panel rendered at the top
    // of the Finalize / Save for Later modal. Same visual
    // treatment as the Modify Quote modal's banner so the
    // two flows feel consistent. Returns null when no
    // banner should render (modal closed / unknown action).
    quotePickerBanner() {
      if (this.pendingPickerAction === "saveForLater") {
        return {
          title: "Save Quote(s) for Later",
          body:
            "Pick which quotes to stash in your Saved Carts. " +
            "Anything you skip stays in your active cart so you " +
            "can keep editing.",
        };
      }
      if (this.pendingPickerAction === "finalize") {
        return {
          title: "Ready to Finalize the Quote?",
          body:
            "Confirm the quotes below — then click <strong>" +
            "Finalize / Print</strong> to lock them in and open " +
            "the printable quote receipt.",
        };
      }
      return null;
    },
    quotePickerOkTitle() {
      var n = this.countSelectedQuoteGroups(this.selectedQuotesForPicker);
      if (this.pendingPickerAction === "saveForLater") {
        if (n === 0) return "Save for Later";
        if (n === 1) return "Save 1 Quote for Later";
        return "Save " + n + " Quotes for Later";
      }
      if (this.pendingPickerAction === "finalize") {
        if (n === 0) return "Finalize / Print";
        if (n === 1) return "Finalize / Print 1 Quote";
        return "Finalize / Print " + n + " Quotes";
      }
      return "Continue";
    },
    // Modify Quote modal options — one row per quote in
    // the cart. Each carries the IDs needed to pre-fill
    // /quotes/create's Step 1-3: companyId, CompanyMachine
    // ID, parent assembly ID, plus the Quote Reference
    // so the user keeps adding to the same Quote. Sub-
    // assembly id is passed through when the original
    // quote drilled into one.
    modifyOptions() {
      // Build the Modify modal's row list with grouped labels.
      // Each backing PurchaseOrder becomes its own pickable row
      // (so the user can edit each assembly's PO Number and
      // Special Instructions independently), but the displayed
      // "Quote #N" label reflects the group index from
      // quoteGroupedOrders rather than the order index in
      // this.orders. Keeps the modal's labelling consistent
      // with the on-page grouped rendering.
      var rows = [];
      var groupIndexByOrderId = {};
      (this.quoteGroupedOrders || []).forEach(function (qg, gIdx) {
        (qg.orders || []).forEach(function (o) {
          groupIndexByOrderId[o.id] = gIdx;
        });
      });

      (this.orders || []).forEach(function (order) {
        var asm = order.assembly || {};
        var machine = asm.machine || {};
        var parentAssemblyId = asm.parentAssemblyId || asm.id || order.assemblyId;
        var leafAssemblyId = asm.parentAssemblyId
          ? (asm.id || order.assemblyId)
          : null;
        var gIdx = groupIndexByOrderId[order.id];
        var labelIdx = gIdx != null ? gIdx + 1 : "?";
        rows.push({
          // PO row id — used as the radio v-model value
          // and as the lookup key for save/convert flows.
          id: order.id,
          // "Purchase Order #{number}" using the actual quote
          // reference; falls back to sequential "Quote #N" if
          // the reference is somehow absent. Mirrors the
          // on-page Purchase Order # convention so any
          // downstream consumer of this row sees the same
          // label the user does.
          quoteLabel: order.number
            ? "Purchase Order #" + order.number
            : "Quote #" + labelIdx,
          poNumber: order.number,
          // Per-PO special instructions — backed by the
          // server-side `purchaseOrders[].specialInstructions`
          // field once the backend wires it. Until then,
          // falls back to "" so the modal still works in
          // session.
          specialInstructions: order.specialInstructions || "",
          machineName: machine.description || "",
          assemblyName: asm.name || "",
          machineId: order.machineId || 0,
          assemblyId: parentAssemblyId,
          subAssemblyId: leafAssemblyId,
          configuredWear: order.configuredWear === true,
          partCount: (order.parts || []).length,
        });
      });
      return rows;
    },
    // Currently-active row in the Modify modal, derived
    // from activeModifyId. Null when nothing is selected
    // (modal hides the editor in that case).
    activeModifyOption() {
      var id = this.activeModifyId;
      if (id == null) return null;
      var match = this.modifyOptions.filter(function (o) {
        return o.id === id;
      });
      return match.length > 0 ? match[0] : null;
    },
  },
  mounted() {
    // ===== RESTORE ACTIVE CART AFTER PARTIAL FINALIZE =====
    // After a partial finalize, finalizeSelectedQuotes stashes
    // the original active cart id under `pendingCartIdRFQ` and
    // overwrites `cartIdRFQ` with the new (finalized) cart's
    // id so SavedRFQ binds to the finalized cart for the
    // print receipt. SavedRFQ then deletes localStorage.
    // cartIdRFQ on render success — orphaning the original
    // active cart.
    //
    // Restore the original pointer here so the leftover quotes
    // come back into view when the user navigates back to
    // /cart/rfqsaveditems. Always clear pendingCartIdRFQ
    // afterwards regardless of whether cartIdRFQ was already
    // set, so a stale pendingCartIdRFQ doesn't override a
    // legitimate later cart.
    try {
      var pending = localStorage.pendingCartIdRFQ;
      if (pending && parseInt(pending, 10) > 0) {
        if (!localStorage.cartIdRFQ
            || parseInt(localStorage.cartIdRFQ, 10) === 0) {
          localStorage.cartIdRFQ = pending;
        }
        delete localStorage.pendingCartIdRFQ;
      }
    } catch (e) {
      // localStorage can throw in incognito / quota contexts;
      // ignore and proceed — worst case the user has to
      // Resume the leftover cart from /account/savedcarts.
    }

    if (localStorage.cartIdRFQ && localStorage.cartIdRFQ > 0) {
      this.cartIdRFQ = localStorage.cartIdRFQ;

      if (this.$route.params.fullOrder) {
        this.fullOrder = this.$route.params.fullOrder;
        this.saveRFQ();
      } else {
        this.getCart();
      }
    } else {
      if (this.$route.params.fullOrder && localStorage.companyId) {
        this.fullOrder = this.$route.params.fullOrder;
        this.saveRFQ();
      }
    }
    this.loadWearPartIds();
    this.loadCompanyWearPartIds();

    // ===== ENTRY-POINT MODAL TRIGGER =====
    // Query param ?openModify=<context> opens the Modify
    // Quote modal with the right context. Currently the
    // only honored value is 'restored-quote' (Resume from
    // /account/savedcarts on the Quote tab). consumeOpenModifyQuery
    // handles both initial-mount and route-change cases
    // (the route watcher above also calls it).
    this.consumeOpenModifyQuery();
  },
  data() {
    return {
      // Map of PartId → true for the GLOBAL wear-eligible
      // catalog (wear parts across ALL companies). Loaded on
      // mount and consulted by goToSearch()/quoteHasNonWearPart()
      // to decide whether to pass allParts=true when the quote
      // contains any non-wear part. This MUST stay global so the
      // allParts search-filter decision is unchanged.
      wearPartIds: {},
      // Map of PartId → true for THIS company's own wear parts
      // (its dbo.Wears = the Account Setup wear list). Drives
      // ONLY the "2% Discount" badge via isWearEligible(), so
      // the chip reflects the customer's own wear list rather
      // than the global catalog. Loaded on mount via
      // loadCompanyWearPartIds().
      companyWearPartIds: {},
      cartIdRFQ: 0,
      companyId: localStorage.companyId,
      companyName: "",
      poNumber: localStorage.poNumberRFQ,
      editPoNumber: false,
      configuredWear: true,
      partsList: [],
      machineId: 0,
      orders: [],
      // savingQuote — set true while saveQuoteForLater is
      // in flight so the button can be disabled and we
      // don't fire the API twice on a double-click.
      savingQuote: false,
      // ===== QUOTE PICKER MODAL =====
      // Single shared modal opened by both Save for Later
      // and Finalize / Print Quote. Lists each quote with
      // a checkbox. The pendingPickerAction string tracks
      // which button opened it so confirmQuotePicker can
      // dispatch to the right handler. Pre-selects all on
      // each open (most common case = "act on everything").
      showQuotePickerModal: false,
      pendingPickerAction: null, // 'saveForLater' | 'finalize'
      selectedQuotesForPicker: [],
      // Modify Quote modal — opens when the user clicks
      // "Modify Quote" in the rfqsaveditems header. Lists
      // each (Quote#, assembly) combo so the user can jump
      // back to /quotes/create with the matching Steps 1-3
      // pre-filled, keeping the same Quote Reference.
      showModifyModal: false,
      // Modify / Convert Quote modal editor state.
      // activeModifyId holds the PO row id currently
      // selected in the modal's quote picker. The two
      // *Draft fields hold pending edits that get
      // committed on Save Changes (so the user can
      // discard via Cancel without polluting orders[]).
      // applyInstructionsToAll, when true, copies the
      // draft Special Instructions onto every PO on
      // save instead of just the active one.
      activeModifyId: null,
      modifyPoNumberDraft: "",
      // Print-header logo error fallback. Flipped true by
      // onPrintLogoError when the <img> fails to load,
      // swapping the markup to a text "STEELE" fallback
      // so the printed quote always carries a brand mark.
      printLogoFailed: false,
      // ===== MODIFY MODAL CONTEXT =====
      // String discriminator describing why the Modify Quote
      // modal is open. Set by whichever caller triggers the
      // modal and read by the banner template to render the
      // right title + subtitle. Cleared on close.
      //
      // Values:
      //   'restored-quote' — user just resumed a quote cart
      //     from /account/savedcarts; banner reads "Quote
      //     Restored Successfully".
      //   'modifying-quote' — user clicked Modify Quote from
      //     the page header; banner reads "Modify Your Quote".
      //   'saving-quote' — user clicked Save for Later
       //     (banner-version path; the existing quote picker
      //     handles selection separately); banner reads
      //     "Save Quote(s) for Later".
      //   'placing-quote' — user clicked Finalize / Print
      //     Quote (banner-version path); banner reads
      //     "Ready to Finalize the Quote?".
      //   'converting' — user clicked Convert to Order;
      //     banner reads "Convert Quote to Order".
      //   null — modal closed.
      modifyContext: null,
      // Single-shot queue for ?openModify=<context> when the
      // query lands before getCart has populated this.orders.
      _pendingOpenModifyContext: null,
      // ===== MODIFY MODAL EXPAND STATE =====
      // Map of qgroup key (Quote # index) → bool. When true,
      // that group's assembly rows + parts lists are visible
      // in the picker. All groups start collapsed on each
      // modal open so the user sees an overview first, then
      // drills into the one they want to edit. Reset on
      // modal close via onModifyModalClose.
      expandedModifyGroups: {},
      modifySpecialInstructionsDraft: "",
      applyInstructionsToAll: false,
      savingModify: false,
      convertingFromModify: false,
      total: 0,
      fullOrder: "",
      SpecialInstructions: "",
      search: {
        PONumber: "",
        keyPartNumber: "",
      },
      displayParts: {
        checked: [],
      },
      partsFields: [
        "item",
        "code",
        "description",
        { key: "quantityPerMachine", label: "QTY PER MACHINE" },
        "price",
        { key: "quantity", label: "Cart Qty" },
        "cost",
        { key: "remove", label: "" },
      ],
      clear: "",
      cartError: false,
      // ===== Per-quote expand state =====
      // Compact pill view is the default for each quote card.
      // Clicking the + button on a card flips its entry here
      // to true, revealing the full editable b-table beneath
      // for inline qty edits / part removal.
      // Keyed by order.id so different quotes can be expanded
      // independently (though typically only one at a time).
      expandedQuotes: {},

      // ===== Convert-to-Order modal state =====
      // showConvertModal toggles the b-modal at the bottom of
      // the template. selectedQuotesForConversion holds the
      // PO ids the user has currently checked. Reset and
      // pre-populated each time createOrder() opens the modal.
      showConvertModal: false,
      selectedQuotesForConversion: [],

      // ===== Clear Quotes modal state =====
      // Same shape and lifecycle as showConvertModal but the
      // inverse action — drives the partial-deletion picker
      // that opens when "Clear Quotes" is clicked with 2+
      // quotes present. Defaults to an empty selection so the
      // user must explicitly opt in to each deletion. Unlike
      // the convert modal (which pre-selects all because
      // "convert everything" is the safe common case), this is
      // a destructive action where opt-in beats opt-out.
      showClearQuotesModal: false,
      selectedQuotesForClear: [],
    };
  },
  watch: {
    // When the user picks a different quote in the
    // Modify modal's radio list, refresh the editor
    // drafts to reflect that quote's current values.
    // Without this, the drafts would persist from
    // whichever quote was edited first and silently
    // overwrite siblings on Save.
    activeModifyId(newId) {
      if (newId == null) {
        this.modifyPoNumberDraft = "";
        this.modifySpecialInstructionsDraft = "";
        return;
      }
      var match = (this.modifyOptions || []).filter(function (o) {
        return o.id === newId;
      });
      if (match.length > 0) {
        this.modifyPoNumberDraft = match[0].poNumber || "";
        this.modifySpecialInstructionsDraft = match[0].specialInstructions || "";
      }
    },

    // Watch the route's openModify query so a Resume click
    // while ALREADY on /cart/rfqsaveditems still triggers
    // the modal. Vue Router reuses the same component on
    // same-route navigation, so mounted() doesn't fire.
    "$route.query.openModify"(newCtx, oldCtx) {
      if (newCtx && newCtx !== oldCtx) {
        this.consumeOpenModifyQuery();
      }
    },

    // When this.orders finally populates (after the cart
    // load), drain any pending openModify context queued
    // by consumeOpenModifyQuery while the cart was empty.
    orders() {
      if (this._pendingOpenModifyContext && (this.orders || []).length > 0) {
        var ctx = this._pendingOpenModifyContext;
        this._pendingOpenModifyContext = null;
        this.applyOpenModifyContext(ctx);
      }
    },
  },
  methods: {
    // Toggle the compact-vs-expanded view for a single quote.
    // Uses Vue.set so newly-added keys are reactive (plain
    // assignment to an object doesn't trigger a re-render).
    toggleQuoteExpand(orderId) {
      var current = !!this.expandedQuotes[orderId];
      this.$set(this.expandedQuotes, orderId, !current);
    },
    getCart() {
      apiService
        .getCart({
          id: this.cartIdRFQ,
          fullOrder: this.fullOrder,
          companyId: this.companyId,
          SpecialInstructions: this.SpecialInstructions,
          CreateQuote: true,
        })
        .then((request) => {
          this.getRFQSuccessful(request);
          this.getSubTotals();
          this.getTotal();
          this.getCompany();
        })
        .catch((err) => this.getRFQFailed(err));
    },
    getRFQSuccessful(request) {
      (this.cartIdRFQ = request.id),
        (this.fullOrder = request),
        (this.orders = request.purchaseOrders),
        (this.SpecialInstructions = request.specialInstructions);
    },
    getRFQFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    saveRFQ() {
      // ===== DELETE-RECREATE PATTERN =====
      // Why we don't just call saveCart({ id: cartIdRFQ }):
      // SaveOldCart server-side runs DoesPartExistInTheCart
      // before saving and returns 400 ("This part is already
      // added. Please adjust the quantity in the Cart.") when
      // ANY incoming PO matches a row already on the cart.
      // That check exists to prevent duplicate adds in the
      // "add to cart" flow, but for the saveRFQ-on-mount case
      // the route's fullOrder is a SNAPSHOT of the existing
      // cart (passed through router params from /quotes/create
      // or similar), so by definition every part is already
      // on the cart and the check rejects every save.
      //
      // Result before this fix: the mount-time saveRFQ failed
      // silently, this.orders never refreshed from the
      // server, the Modify modal opened with stale state, and
      // the dev-tools error log filled with 400s on every
      // navigate-back to /cart/rfqsaveditems.
      //
      // Mirroring saveModifyChanges' fix: delete the existing
      // cart row, then save with id=0 so the request routes to
      // SaveNewCart (no duplicate check). The new cart inherits
      // CreateQuote=true and lands as Discriminator=Rfq,
      // identical state to before minus the dropped row.
      //
      // companyId / specialInstructions come from this.* (data
      // fields, populated on mount from localStorage) rather
      // than from this.$route.params. The route params are
      // only set when the user transitions into this page
      // through a specific named route — on a direct load,
      // refresh, or any in-page action that calls saveRFQ
      // (like Convert to Order's confirm flow), the params
      // are undefined, which made the saveCart payload omit
      // companyId entirely and the backend returned 400
      // "Company is required!".
      var self = this;
      var existingCartId = parseInt(self.cartIdRFQ, 10) || 0;

      var doSave = function () {
        return apiService.saveCart({
          id: 0,
          purchaseOrders: self.fullOrder.purchaseOrders,
          companyId: self.companyId
            || self.$route.params.companyId
            || localStorage.companyId,
          specialInstructions:
            self.SpecialInstructions
            || self.$route.params.SpecialInstructions
            || "",
          CreateQuote: true,
        });
      };

      var savePromise;
      if (existingCartId > 0) {
        savePromise = apiService
          .deleteCart({ id: existingCartId })
          .catch(function (e) {
            // Soft-fail: a delete error doesn't block the
            // recreate. Worst case the old cart lingers in
            // /account/savedcarts as a duplicate; the new
            // cart still gets created and is the active one.
            console.warn(
              "[Quote saveRFQ] deleteCart before recreate failed:",
              e
            );
          })
          .then(doSave);
      } else {
        savePromise = doSave();
      }

      savePromise
        .then(function (request) {
          self.saveRFQSuccessful(request);
          // saveRFQSuccessful sets self.cartIdRFQ from the
          // response; mirror that into localStorage so future
          // page loads bind to the freshly-created cart.
          try {
            localStorage.cartIdRFQ = self.cartIdRFQ;
          } catch (e) {
            // Storage disabled — surfaces nowhere critical.
          }
          self.getSubTotals();
          self.getTotal();
          self.getCompany();
        })
        .catch(function (err) {
          self.saveRFQFailed(err);
        });
    },
    saveRFQSuccessful(request) {
      (this.cartIdRFQ = request.id),
        (this.fullOrder = request),
        (this.SpecialInstructions = request.specialInstructions),
        (this.orders = request.purchaseOrders);
    },
    saveRFQFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    getCompany() {
      if (this.companyId) {
        apiService
          .getCompanyDetails({
            id: this.companyId,
          })
          .then((request) => {
            this.getCompanySuccessful(request);
          })
          .catch((err) => this.getCompanyFailed(err));
      }
    },
    getCompanySuccessful(request) {
      this.companyName = request.name;
    },
    getCompanyFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    getSubTotals() {
      this.orders.forEach((order) => {
        
      var subtotal = 0;
        $(order.parts).each(function() {
          subtotal = subtotal + this.part.price * this.quantity;

          order.cost = subtotal;
        });
      });
    },
    updateSubtotal(order) {
      var subtotal = 0;

      if (order.parts.length) {
        for (var i in order.parts) {
          subtotal = subtotal + order.parts[i].part.cost;
          // console.log(order.parts[i]);
        }
        order.subtotal = subtotal;
        this.$forceUpdate(order);
      } else {
        order.cost = 0;
        // this.total = this.total + 0;
        this.$forceUpdate();
      }

      apiService
        .updatePurchaseOrder({
          id: order.id,
          Number: order.number,
          parts: order.parts,
          // SpecialInstructions: this.SpecialInstructions
        })
        .then(() => {
          if(order.parts.length < 1){
            this.deletePO(order);
          }
          this.getSubTotals();
          this.getTotal();
        })
        .catch((err) => this.failed(err));
    },
    failed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    getTotal() {
      var total = 0;
      this.orders.forEach((order) => {
        if (order.cost > 0) {
          total = total + order.cost;
        } else {
          total = total + 0;
        }
      });

      this.total = total;
    },
    // ===== CLEAR QUOTES =====
    // Entry point for the new "Clear Quotes" header button.
    // Branches on count to keep UX light for the common
    // single-quote case while offering granular control when
    // the user has built up multiple quotes:
    //
    //   - 0 quotes:  no-op (button is hidden anyway by v-if)
    //   - 1 quote:   simple msgBoxConfirm → deleteCart
    //   - 2+ quotes: open the partial-selection modal
    //
    // The single-quote path mirrors the original showDeleteModal
    // verbatim so existing user expectations don't shift; only
    // the multi-quote path is new behaviour.
    clearQuotes() {
      if (!this.orders || this.orders.length === 0) return;

      // Single-quote → light confirm. Same shape as the old
      // Delete Quote button so muscle memory holds.
      if (this.orders.length === 1) {
        this.$bvModal
          .msgBoxConfirm(
            "Delete this quote? This cannot be undone.",
            {
              title: "Clear Quote",
              id: "clearOneQuoteModal",
              size: "md",
              buttonSize: "sm",
              okVariant: "danger",
              okTitle: "Delete Quote",
              cancelVariant: "outline-secondary",
              cancelTitle: "Cancel",
              hideHeaderClose: true,
              centered: true,
            }
          )
          .then((clear) => {
            if (!clear) return;
            apiService
              .deleteCart({ id: this.cartIdRFQ })
              .then((request) => this.deleteCartSuccessful(request))
              .catch((err) => this.deleteCartFailed(err));
          })
          .catch((err) => this.deleteCartFailed(err));
        return;
      }

      // 2+ quotes → open the picker. Reset the selection so
      // users start with a clean slate every time the modal
      // opens — destructive opt-in beats opt-out.
      this.selectedQuotesForClear = [];
      this.showClearQuotesModal = true;
    },

    toggleSelectAllQuotesForClear() {
      if (this.selectedQuotesForClear.length === this.orders.length) {
        this.selectedQuotesForClear = [];
      } else {
        this.selectedQuotesForClear = this.orders.map(function (o) {
          return o.id;
        });
      }
    },

    confirmClearQuotes(bvModalEvent) {
      // Manual close pattern: preventDefault keeps the modal
      // open until the API resolves, so an error doesn't drop
      // the user into ambiguity about whether the deletion
      // succeeded.
      if (this.selectedQuotesForClear.length === 0) {
        if (bvModalEvent) bvModalEvent.preventDefault();
        return;
      }
      if (bvModalEvent) bvModalEvent.preventDefault();

      var allSelected =
        this.selectedQuotesForClear.length === this.orders.length;

      // ===== ALL-SELECTED PATH =====
      // Cheap: blow away the whole cart in one API call. Same
      // semantics as the old Delete Quote button. Reuses
      // deleteCartSuccessful so localStorage clears + router
      // pushes to /cart/createrfq just like before.
      if (allSelected) {
        apiService
          .deleteCart({ id: this.cartIdRFQ })
          .then((request) => {
            this.showClearQuotesModal = false;
            this.deleteCartSuccessful(request);
          })
          .catch((err) => {
            this.showClearQuotesModal = false;
            this.deleteCartFailed(err);
          });
        return;
      }

      // ===== PARTIAL PATH =====
      // Loop through selected ids and call deletePoNumber on
      // each. We snapshot the ids up front because deletePO
      // mutates this.orders (splice) which would invalidate
      // an in-flight iterator. After all deletions resolve,
      // the existing deletePO success handler has already
      // taken care of localStorage.poNumberRFQ + cartIdRFQ
      // adjustments, so we just close the modal and refresh
      // totals.
      var idsToDelete = this.selectedQuotesForClear.slice();
      var ordersToDelete = this.orders.filter(function (o) {
        return idsToDelete.indexOf(o.id) !== -1;
      });

      var deletions = ordersToDelete.map((order) => {
        // Mirror deletePO's local state mutations (splice
        // from this.orders, sync localStorage.poNumberRFQ)
        // but call apiService.deletePoNumber directly so we
        // can chain the promises and close the modal only
        // after every one resolves. Wrapping in our own
        // promise also lets us swallow per-row errors and
        // continue with the rest of the batch — better than
        // bailing on the first failure with a half-done
        // delete.
        return new Promise((resolve) => {
          apiService
            .deletePoNumber({ id: order.id })
            .then(() => {
              var idx = this.orders.indexOf(order);
              if (idx !== -1) this.orders.splice(idx, 1);
              resolve({ ok: true, id: order.id });
            })
            .catch(() => {
              resolve({ ok: false, id: order.id });
            });
        });
      });

      Promise.all(deletions).then((results) => {
        // Reconcile localStorage with the post-delete state.
        // Empty cart → drop the active linkage (cartIdRFQ=0)
        // so the create-quote page hides Clear Cart, matching
        // the existing deletePO behaviour for the last-PO
        // case.
        if (this.orders.length > 0) {
          var lastNumber = this.orders[this.orders.length - 1].number;
          localStorage.poNumberRFQ = lastNumber;
          this.poNumber = lastNumber;
        } else {
          delete localStorage.poNumberRFQ;
          localStorage.cartIdRFQ = 0;
          this.poNumber = null;
        }

        this.getSubTotals();
        this.getTotal();

        // Surface partial-failure to the user so they don't
        // walk away thinking everything's deleted when one
        // PO actually failed. Successful-count info nudges
        // them to retry just the failed ids if they wish.
        var failed = results.filter(function (r) {
          return !r.ok;
        });
        if (failed.length > 0) {
          this.$bvToast.toast(
            failed.length +
              " quote(s) couldn't be deleted. Please try again.",
            {
              title: "Partial deletion",
              autoHideDelay: 6000,
              variant: "warning",
              solid: true,
            }
          );
        } else {
          this.$bvToast.toast(
            idsToDelete.length + " quote(s) deleted.",
            {
              title: "Quotes Cleared",
              autoHideDelay: 4000,
              variant: "success",
              solid: true,
            }
          );
        }

        this.showClearQuotesModal = false;
        this.selectedQuotesForClear = [];

        // Notify the global cart bus so the header badge and
        // the SavedCarts page re-fetch — same pattern other
        // mutating actions use across the app.
        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }
      });
    },

    // Navigate back to the Search page using the search
    // context from the most recently added quote PO. Reads
    // straight from this.orders (which was loaded from the
    // quote cart on mount) — no extra API call.
    //
    // CRITICAL: we clear localStorage.code and
    // localStorage.searchTerm before navigating. The Search
    // page's searchResults() does:
    //
    //   searchCode: this.searchCode || localStorage.code || null
    //
    // ...so if our URL params don't carry a code through
    // (missing field, encoding hiccup, anything), the search
    // page silently falls back to whatever the user's last
    // header-bar search left in localStorage — which is
    // typically the part they were searching for when they
    // were adding orders. That's exactly the "wrong context"
    // symptom: URL says nothing, search runs against stale
    // localStorage.code, user lands on the previous order's
    // part. Clearing those keys forces the search page to
    // either honor what we send or show no search at all.
    //
    // We also pass `from=quote` in the URL so the Search
    // page knows to load the quote cart's staging tray
    // (cartIdRFQ) instead of the order cart's. Without this
    // signal Search defaults to order context — which would
    // surface ORDER staging on the right rail even though the
    // user just came from the quotes page. The flag is read
    // by Search's mount logic; see getCart() over there.
    // Pull the wear-eligible PartIds for the current company
    // so goToSearch can decide whether to pass allParts=true
    // (preventing the Search page from filtering this quote's
    // own non-wear contents out of the results on return).
    // Defensive against missing endpoint, missing companyId,
    // bad response shapes, network failure.
    loadWearPartIds() {
      if (!apiService.getWearPartIds) {
        this.wearPartIds = {};
        return;
      }
      var companyId = localStorage.companyId || "";
      if (!companyId) {
        this.wearPartIds = {};
        return;
      }
      var self = this;
      apiService
        .getWearPartIds({ companyId: companyId })
        .then(function (data) {
          var map = {};
          if (Array.isArray(data)) {
            data.forEach(function (id) {
              if (id != null) map[id] = true;
            });
          }
          self.wearPartIds = map;
        })
        .catch(function () {
          self.wearPartIds = {};
        });
    },

    // Pull THIS company's own wear PartIds (its dbo.Wears = the
    // Account Setup wear list) into companyWearPartIds, used
    // ONLY by isWearEligible() for the "2% Discount" badge so
    // the chip reflects the customer's own wear list rather than
    // the global wear-eligible catalog. Kept SEPARATE from the
    // global wearPartIds map above, which still drives the
    // allParts search-filter decision (quoteHasNonWearPart) and
    // must not change. Failure (or a pre-deploy 404 on the new
    // endpoint) → empty map (badge simply doesn't render).
    loadCompanyWearPartIds() {
      if (!apiService.getCompanyWearPartIds) {
        this.companyWearPartIds = {};
        return;
      }
      var companyId = localStorage.companyId || "";
      if (!companyId) {
        this.companyWearPartIds = {};
        return;
      }
      var self = this;
      apiService
        .getCompanyWearPartIds(companyId)
        .then(function (data) {
          var map = {};
          if (Array.isArray(data)) {
            data.forEach(function (id) {
              if (id != null) map[id] = true;
            });
          }
          self.companyWearPartIds = map;
        })
        .catch(function () {
          self.companyWearPartIds = {};
        });
    },

    // True if any part anywhere in the user's active quote is
    // NOT in the company's wear-parts catalog. Drives the
    // allParts=true URL flag from goToSearch — so returning
    // to search after working with a quote that has non-wear
    // content doesn't pre-filter to wear-only and hide it.
    quoteHasNonWearPart() {
      if (!this.orders || this.orders.length === 0) return false;
      // Map not loaded → default to TRUE. Safer to surface
      // all parts than to silently filter the quote out.
      if (!this.wearPartIds || Object.keys(this.wearPartIds).length === 0) {
        return true;
      }
      for (var i = 0; i < this.orders.length; i++) {
        var po = this.orders[i];
        if (!po.parts) continue;
        for (var j = 0; j < po.parts.length; j++) {
          var p = po.parts[j];
          var pid = p && p.part ? (p.part.id || p.part.partId) : null;
          if (pid != null && !this.wearPartIds[pid]) {
            return true;
          }
        }
      }
      return false;
    },

    // ===== MODIFY / CONVERT QUOTE MODAL =====
    // The modal lets the user edit a quote's PO Number
    // and Special Instructions, optionally apply
    // instructions to all quotes in one shot, add more
    // parts to the picked quote, or convert it to an
    // order. Per-PO Special Instructions depends on the
    // backend exposing `purchaseOrders[].specialInstructions`
    // — until that's wired, the field round-trips
    // through this.orders[].specialInstructions on the
    // client but doesn't survive a refresh.

    // Logo <img> in the print header failed to load —
    // flip the flag so the v-if swaps to the text
    // fallback ("STEELE") visible in the next paint.
    onPrintLogoError() {
      this.printLogoFailed = true;
    },

    // ===== PRINT-TO-PDF TRIGGER =====
    // Calls the browser's native print dialog. Used by
    // the page-level Print Quote button if/when the user
    // has finalized the quote inline. Currently the
    // primary print path is via SavedRFQ.vue's print
    // button after finalization.
    printOrder() {
      window.print();
    },

    // ===== CONTEXT-AWARE MODAL OPENERS =====
    // Each opener sets modifyContext + showModifyModal in
    // one step. Templates / external callers should always
    // use one of these rather than touching showModifyModal
    // directly, so the banner copy and post-confirm
    // dispatch always match the actual entry point.

    // Resume from /account/savedcarts (Quote tab). Triggered
    // on /cart/rfqsaveditems mount when the route arrives
    // with ?openModify=restored-quote.
    openModifyForRestore() {
      this.modifyContext = "restored-quote";
      this.showModifyModal = true;
    },

    // Modify Quote header button — pure review/edit. Default
    // entry point when the user clicks the header's pencil-
    // icon button.
    openModifyForModify() {
      this.modifyContext = "modifying-quote";
      this.showModifyModal = true;
    },

    // Generic / legacy opener — preserved for any callsite
    // that still uses it (e.g. the existing openModifyModal
    // calls in template). Defaults to 'modifying-quote'
    // context. New callsites should use a context-specific
    // opener.
    openModifyModal() {
      if (!this.modifyContext) this.modifyContext = "modifying-quote";
      this.showModifyModal = true;
    },

    onModifyModalOpen() {
      // Default to the first quote whenever the modal
      // opens so the editor isn't blank. Watcher on
      // activeModifyId then primes the drafts.
      var first = (this.modifyOptions || [])[0];
      this.activeModifyId = first ? first.id : null;
      this.applyInstructionsToAll = false;
      // All groups start collapsed each time the modal
      // opens — gives the user an overview first, then
      // they expand the one they want to edit.
      this.expandedModifyGroups = {};
    },

    // Flip the expand state for a single Quote # group in
    // the picker. $set so the new key triggers Vue
    // reactivity (plain property assign on a plain object
    // wouldn't re-render).
    toggleModifyGroup(key) {
      var current = !!this.expandedModifyGroups[key];
      this.$set(this.expandedModifyGroups, key, !current);
    },

    // ===== QUOTE-GROUP SELECTION HELPERS =====
    // The Finalize / Save for Later / Convert / Clear
    // pickers iterate quoteGroupedOrders (one row per
    // Quote #) but the underlying selection arrays still
    // hold the backing PurchaseOrder ids, because that's
    // what confirmQuotePicker / confirmConvertToOrder /
    // clear-quotes flows expect on the backend.
    //
    // These helpers bridge the two: a Quote # checkbox
    // is "checked" if every backing order id under that
    // group is in the selection array; "unchecked" if
    // none are; "partial" if some are. Toggling the
    // checkbox flips ALL of the group's ids in/out of
    // the array as a unit.
    isQuoteGroupFullySelected(qgroup, selection) {
      if (!qgroup || !qgroup.orders || qgroup.orders.length === 0) return false;
      for (var i = 0; i < qgroup.orders.length; i++) {
        if (selection.indexOf(qgroup.orders[i].id) === -1) return false;
      }
      return true;
    },
    isQuoteGroupPartiallySelected(qgroup, selection) {
      if (!qgroup || !qgroup.orders || qgroup.orders.length === 0) return false;
      var hits = 0;
      for (var i = 0; i < qgroup.orders.length; i++) {
        if (selection.indexOf(qgroup.orders[i].id) !== -1) hits++;
      }
      return hits > 0 && hits < qgroup.orders.length;
    },
    setQuoteGroupSelection(qgroup, checked, selectionField) {
      // selectionField is the string name of the data field
      // holding the array (e.g. 'selectedQuotesForPicker')
      // so the same helper drives all three pickers.
      var current = (this[selectionField] || []).slice();
      var ids = (qgroup.orders || []).map(function (o) { return o.id; });

      if (checked) {
        ids.forEach(function (id) {
          if (current.indexOf(id) === -1) current.push(id);
        });
      } else {
        current = current.filter(function (id) {
          return ids.indexOf(id) === -1;
        });
      }
      this[selectionField] = current;
    },

    // Count of distinct Quote # groups represented in a
    // selection array. Used by the OK-button labels (e.g.
    // "Finalize / Print 1 Quote" vs "2 Quotes") so the
    // count matches what the user sees in the picker —
    // one Quote # group with 2 backing assemblies counts
    // as 1 quote, not 2.
    countSelectedQuoteGroups(selection) {
      if (!selection || selection.length === 0) return 0;
      var n = 0;
      (this.quoteGroupedOrders || []).forEach(function (qg) {
        var hasAny = (qg.orders || []).some(function (o) {
          return selection.indexOf(o.id) !== -1;
        });
        if (hasAny) n++;
      });
      return n;
    },

    // Reset modal state when the modal closes. Without
    // this hook, the next open could inherit stale draft
    // text or context from the previous session.
    onModifyModalClose() {
      this.activeModifyId = null;
      this.modifyPoNumberDraft = "";
      this.modifySpecialInstructionsDraft = "";
      this.applyInstructionsToAll = false;
      this.modifyContext = null;
      this.expandedModifyGroups = {};
    },

    // ===== ENTRY-POINT QUERY CONSUMER =====
    // Reads ?openModify=<context> from the current route
    // and opens the modal with the matching context. Wired
    // through mounted() and a $route watcher so re-navigation
    // to /cart/rfqsaveditems while already there still
    // triggers it (Vue reuses the component).
    consumeOpenModifyQuery() {
      var ctx = this.$route.query.openModify;
      if (!ctx) return;
      if (ctx === "restored-quote") {
        if ((this.orders || []).length > 0) {
          this.openModifyForRestore();
        } else {
          this._pendingOpenModifyContext = ctx;
        }
      }
      // Strip the query param so back-nav doesn't replay.
      var preservedQuery = Object.assign({}, this.$route.query);
      delete preservedQuery.openModify;
      this.$router
        .replace({ path: this.$route.path, query: preservedQuery })
        .catch(function () {});
    },

    applyOpenModifyContext(ctx) {
      switch (ctx) {
        case "restored-quote":
          this.openModifyForRestore();
          break;
      }
    },

    // Persist the edited PO number + Special Instructions
    // on the active quote (or, when applyInstructionsToAll
    // is true, broadcast the instructions to every quote).
    // PO number renames continue to use the existing
    // updatePurchaseOrder API. Special Instructions
    // currently piggyback on purchaseOrders[].specialInstructions
    // in the saveCart payload — the server should ignore
    // unknown fields gracefully today and start persisting
    // them once you wire the backend.
    saveModifyChanges() {
      if (!this.activeModifyOption) return;
      if (this.savingModify) return;
      this.savingModify = true;

      var self = this;
      var activeId = this.activeModifyId;
      var newPo = (this.modifyPoNumberDraft || "").trim();
      var newInstr = this.modifySpecialInstructionsDraft || "";
      var applyAll = this.applyInstructionsToAll === true;

      // Snapshot the active row's CURRENT Quote Reference (PO
      // Number) before the loop mutates anything. Sibling rows
      // sharing this value will be migrated to the new value
      // alongside the active row — see the loop body for why.
      var activeOrder = this.orders.find(function (o) {
        return o.id === activeId;
      });
      var activePoNumber = activeOrder ? activeOrder.number : null;

      // Mutate this.orders in place so the page reflects
      // the new values immediately. saveRFQ below then
      // pushes the full cart to the server.
      this.orders.forEach(function (order) {
        if (order.id === activeId) {
          // PO number — only push if changed and non-empty.
          if (newPo && newPo !== order.number) {
            order.number = newPo;
          }
          // Per-quote Special Instructions.
          self.$set(order, "specialInstructions", newInstr);
        } else if (
          activePoNumber &&
          order.number === activePoNumber
        ) {
          // ===== SIBLING ASSEMBLIES UNDER THE SAME QUOTE =====
          // Quote Reference rename — ALWAYS propagated. The PO
          // Number / Quote Reference is the Quote's identifier;
          // typing a new one renames the Quote, not forks one
          // assembly off into a separate Quote. Without this,
          // changing Quote #3's reference from 103 → 105 left
          // sibling assemblies on 103 and created a phantom
          // Quote #4 for just the edited row. Now the entire
          // Quote moves together.
          //
          // Special Instructions are NOT propagated here — that's
          // intentional, SI varies per-assembly more often than
          // Quote References do. Use "Apply to all N quotes" if
          // you want to broadcast SI.
          if (newPo && newPo !== order.number) {
            order.number = newPo;
          }
        } else if (applyAll) {
          // Broadcast — only the instructions, not the PO.
          self.$set(order, "specialInstructions", newInstr);
        }
      });

      // ===== DELETE-RECREATE PATTERN =====
      // Why we don't just call saveCart({ id: cartIdRFQ }):
      // SaveOldCart server-side runs DoesPartExistInTheCart
      // before saving, returning 400 ("This part is already
      // added. Please adjust the quantity in the Cart.") for
      // any incoming PO whose parts are already on the cart.
      // That check is meant for the "add to cart" flow where
      // the user is adding a NEW part — not for the modify
      // flow where every part is already there by definition.
      //
      // Mirroring cart.vue's saveModifyChanges: delete the
      // existing cart row, then save with id=0 so the request
      // routes to SaveNewCart (no duplicate check). The new
      // cart inherits CreateQuote=true and lands as
      // Discriminator=Rfq, identical state to before the
      // edit minus the dropped row.
      var existingCartId = parseInt(self.cartIdRFQ, 10) || 0;

      var doSave = function () {
        return apiService.saveCart({
          id: 0,
          purchaseOrders: self.orders,
          companyId: self.companyId
            || self.$route.params.companyId
            || localStorage.companyId,
          // Cart-level Special Instructions — kept blank now
          // that per-PO instructions are the source of truth.
          specialInstructions: "",
          CreateQuote: true,
        });
      };

      var savePromise;
      if (existingCartId > 0) {
        savePromise = apiService
          .deleteCart({ id: existingCartId })
          .catch(function (e) {
            // Soft-fail: a delete error doesn't block the
            // recreate. Worst case the old cart lingers in
            // /account/savedcarts as a duplicate; the new
            // cart still gets created and is the active one.
            console.warn(
              "[Quote Modify] deleteCart before recreate failed:",
              e
            );
          })
          .then(doSave);
      } else {
        savePromise = doSave();
      }

      savePromise
        .then(function (request) {
          self.savingModify = false;
          // Capture the new cart id so subsequent operations
          // (further modifies, save-for-later, finalize) target
          // the freshly-created cart, not the deleted one.
          if (request && request.id) {
            self.cartIdRFQ = request.id;
            try {
              localStorage.cartIdRFQ = request.id;
            } catch (e) {
              // Storage disabled — surfaces nowhere critical.
            }
          }
          // Refresh from response so client and server
          // are aligned (handles any normalization the
          // backend applies to PO numbers, instructions,
          // etc.).
          if (request) {
            self.fullOrder = request;
            if (request.purchaseOrders) {
              self.orders = request.purchaseOrders;
            }
          }
          self.$bvToast.toast(
            applyAll
              ? "Saved. Special Instructions applied to all quotes."
              : "Saved.",
            {
              title: "Quote Updated",
              autoHideDelay: 3000,
              variant: "success",
              solid: true,
            }
          );
          self.showModifyModal = false;
        })
        .catch(function (err) {
          self.savingModify = false;
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : "";
          self.$bvToast.toast(
            "Could not save changes. " + (traceId ? "TraceID: " + traceId : ""),
            {
              title: "Error",
              autoHideDelay: 5000,
              variant: "danger",
              solid: true,
            }
          );
        });
    },

    // "Add More Parts" — same pre-fill flow that used to
    // be the entire purpose of this modal. Routes to
    // /quotes/create with modify* query params so the
    // create page lands on the picked quote's machine /
    // assembly with the same Quote Reference.
    goAddMoreParts() {
      var opt = this.activeModifyOption;
      if (!opt) return;
      this.showModifyModal = false;
      var query = {
        modifyCompanyId: this.companyId,
        modifyMachineId: opt.machineId,
        // ===== Why machineName is required here =====
        // opt.machineId is the catalog Machine.Id (sourced from
        // PurchaseOrder.MachineId in modifyOptions), but Step 2's
        // dropdown on /quotes/create is keyed on CompanyMachine.Id
        // — the two are different IDs that share a column name.
        // Without modifyMachineName, the create page receives an
        // ID that doesn't match any item in its loaded machines
        // list, validateMachineSelection clears it, and Step 2
        // silently shows "Select your machine" even though the
        // user came here from a specific machine context.
        //
        // Passing the description text lets applyModifyQuery
        // resolve to the correct CompanyMachine.Id by looking up
        // machine.text in the dropdown's options. See
        // pendingModifyMachineName in Create-RFQ.vue's
        // data() and getMachinesByCompany.then for the
        // consumer side.
        modifyMachineName: opt.machineName,
        modifyAssemblyId: opt.assemblyId,
        modifyPoNumber: opt.poNumber,
        modifyConfiguredWear: opt.configuredWear ? "1" : "0",
      };
      if (opt.subAssemblyId) {
        query.modifySubAssemblyId = opt.subAssemblyId;
      }
      this.$router.push({
        name: "Create New Quote",
        query: query,
      });
    },

    // Convert this RFQ cart to an order. Always full-convert
    // (the entire saved quote becomes the active order) —
    // the proven, working API path used by RFQDetails.vue's
    // convert button and by submitCart in this same file.
    //
    // History: this method previously called
    //   apiService.convertQuoteToOrder(cartId, poIds)
    // intending to support partial convert (subset of POs
    // becomes an order, rest stay as a saved quote). That
    // method does not exist on FeApiService — calling it
    // threw a synchronous TypeError before the .then().catch()
    // chain attached, so neither handler ran and the button
    // stayed stuck on "Converting…" with no error feedback.
    //
    // Switched to apiService.rfqOrder({ id }) which is the
    // live, working full-convert path. Trade-off: the
    // partial-convert UX (Convert button on a single PO row
    // when the cart has multiple) now becomes "convert all
    // quotes in this cart" instead of "convert just this
    // one". The dedicated multi-quote picker modal that
    // would have driven true partial convert
    // (createOrder / confirmConvertToOrder) is not currently
    // wired to any UI button — that path is dead code today.
    // If true partial convert is wanted later, it needs a
    // backend endpoint + a wrapper added to FeApiService.js.
    //
    // Wrapped the call in try/catch so any future synchronous
    // throw resets the button state — protects against the
    // exact silent-hang failure mode that prompted this fix.
    convertFromModify() {
      var opt = this.activeModifyOption;
      if (!opt) return;
      if (this.convertingFromModify) return;
      this.convertingFromModify = true;

      var self = this;
      var resetState = function () {
        self.convertingFromModify = false;
      };
      var showError = function (err) {
        var traceId =
          err && err.error && err.error.response && err.error.response.data
            ? err.error.response.data.traceId
            : "";
        self.$bvToast.toast(
          "Could not convert quote to order. " +
            (traceId ? "TraceID: " + traceId : ""),
          {
            title: "Conversion Failed",
            autoHideDelay: 5000,
            variant: "danger",
            solid: true,
          }
        );
      };

      try {
        apiService
          .rfqOrder({ id: this.cartIdRFQ })
          .then(function (response) {
            resetState();
            var newCartId = (response && response.id) || self.cartIdRFQ;
            localStorage.cartId = newCartId;
            // Full convert always — the original RFQ row is
            // now the order row (same db record, flipped
            // discriminator), so the RFQ pointer is stale.
            try { delete localStorage.cartIdRFQ; } catch (e) {}
            self.showModifyModal = false;
            self.$router.push({
              name: "Cart",
              params: {
                cartId: newCartId,
                order: self.fullOrder,
                purchaseOrders: self.fullOrder
                  ? self.fullOrder.purchaseOrders
                  : [],
                companyId: self.companyId,
                fullOrder: self.fullOrder,
                SpecialInstructions: "",
              },
            });
          })
          .catch(function (err) {
            resetState();
            showError(err);
          });
      } catch (err) {
        // Synchronous throw — e.g. the api method goes
        // missing in a future refactor. Without this
        // guard the button would hang indefinitely.
        resetState();
        showError(err);
        console.error("[convertFromModify] sync error:", err);
      }
    },

    goToSearch() {
      var query = { from: "quote" };

      // If the user's quote contains any non-wear part, signal
      // the Search page to disable Show Wear Parts Only on
      // arrival. They were almost certainly searching with
      // the wear filter off when they added those parts;
      // returning with it back on would hide their content.
      if (this.quoteHasNonWearPart()) {
        query.allParts = "true";
      }

      var lastOrder = (this.orders && this.orders.length > 0)
        ? this.orders[this.orders.length - 1]
        : null;

      if (lastOrder) {
        var firstPart = (lastOrder.parts && lastOrder.parts.length > 0)
          ? lastOrder.parts[0]
          : null;
        if (firstPart && firstPart.part) {
          var code = firstPart.part.code || firstPart.part.item;
          if (code) query.code = code;
        }

        if (lastOrder.assembly) {
          if (lastOrder.assembly.machine
              && lastOrder.assembly.machine.description) {
            query.machineName = lastOrder.assembly.machine.description;
          }
          if (lastOrder.assembly.name) {
            query.assembly = lastOrder.assembly.name;
          }
        }
      }

      // Clear stale fallback values before navigating. See
      // commentary above for why this matters.
      try {
        delete localStorage.code;
        delete localStorage.searchTerm;
        delete localStorage.description;
      } catch (e) {
        // localStorage access can throw in incognito / quota
        // contexts; ignore — worst case we just get the old
        // fallback behaviour we're trying to avoid.
      }

      this.$router.push({ name: "Search", query: query });
    },

    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(
          "PLEASE CONFIRM YOU WANT TO DELETE ALL ITEMS IN CART",
          {
            title: "PLEASE CONFIRM",
            id: "deleteModal",
            size: "md",
            buttonSize: "sm",
            okVariant: "dark",
            okTitle: "Yes, please clear cart",
            cancelVariant: "outline",
            cancelTitle: "No, please don’t",
            hideHeaderClose: true,
            centered: true,
          }
        )
        .then((clear) => {
          if (clear) {
            apiService
              .deleteCart({
                id: this.cartIdRFQ,
              })
              .then((request) => {
                this.deleteCartSuccessful(request);
              })
              .catch((err) => this.deleteCartFailed(err));
          }
        })
        .catch((err) => this.deleteCartFailed(err));
    },
    deleteCartSuccessful() {
      // Clear cart-scoped pointers only. Do NOT delete
      // localStorage.companyId — that key is shared with
      // the Header (Hello / Company greeting), the company
      // selector, and authenticated-context resolution
      // throughout the app. Deleting it as a side effect
      // of a cart action made users appear logged out
      // (the company-bound greeting disappears, the navbar
      // reverts, and Create-page guards bounce them).
      // Cart deletion has nothing to do with which company
      // the user is operating in, so leaving companyId
      // alone is correct.
      delete localStorage.poNumberRFQ;
      delete localStorage.cartIdRFQ;
      delete localStorage.machineId;
      delete localStorage.assemblyId;

      // Notify the global cart bus so Head.vue's
      // loadCartCount fires and the Quotes badge in the
      // header refreshes. Without this, the badge keeps
      // showing the pre-delete count until a navigation
      // remounts Head. Both the single-quote and
      // all-selected delete paths converge here, so one
      // emit covers both.
      if (this.$cartBus) {
        this.$cartBus.$emit("cart-updated");
      }

      this.$router.push({
        name: "Create New Quote",
      });
    },
    deleteCartFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    // ===== isWearEligible =====
    // Returns true when the given part appears in THIS
    // company's own wear parts map (companyWearPartIds,
    // loaded on mount via apiService.getCompanyWearPartIds).
    // Drives the "2% Discount" chip rendered next to wear
    // part codes inside the Modify Quote modal. Uses the
    // company-scoped map (not the global wearPartIds, which
    // still drives the allParts search-filter decision) so
    // the chip reflects the customer's own Account Setup
    // wear list. Mirrors the helper of the same name on
    // Index_-_Cart.vue.
    //
    // Accepts either a CartPart (p.part.id, p.partId) or
    // a raw part object (p.id) so callers don't have to
    // remember which shape they have.
    isWearEligible(part) {
      if (!part) return false;
      var map = this.companyWearPartIds || {};
      var pid =
        (part.part && part.part.id) ||
        part.partId ||
        part.id ||
        null;
      if (pid == null) return false;
      return !!map[pid];
    },
    removeItem(item, order) {
      var removeIndex = order.parts
        .map(function(item) {
          return item.id;
        })
        .indexOf(item.id);
      order.parts.splice(removeIndex, 1);
      this.$forceUpdate();
      this.$forceUpdate();
    },
    removePoNumber() {
      delete localStorage.poNumberRFQ;
      this.$router.push({
        name: "Create New Quote",
        params: {
          companyId: this.companyId,
          fullOrder: this.fullOrder,
        },
      });
    },
    useSamePoNumber() {
      this.$router.push({
        name: "Create Quote - Choose Assembly",
        params: {
          number: this.poNumber,
          machineId: this.machineId,
          configuredWear: this.configuredWear,
          fullOrder: this.fullOrder,
        },
      });
    },
    // ===== QUOTE PICKER MODAL — OPENER + DISPATCH =====
    // Both "Save for Later" and "Finalize / Print Quote"
    // funnel through the same picker modal so the user
    // can choose which subset of their quotes the action
    // applies to. The action is identified by the string
    // passed in here, stashed on pendingPickerAction, and
    // consumed by confirmQuotePicker when the user clicks
    // the modal's OK button.
    openQuotePicker(action) {
      if (!this.cartIdRFQ || !this.orders || this.orders.length === 0) {
        this.$bvToast.toast(
          "Your quote cart is empty — there is nothing to " +
            (action === "finalize" ? "finalize." : "save."),
          {
            title:
              action === "finalize" ? "Nothing to Finalize" : "Empty Quote",
            autoHideDelay: 4000,
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      // Pre-select all quotes — most common case is
      // "act on everything" and we want that to be a
      // single click of the confirm button.
      this.selectedQuotesForPicker = this.orders.map(function (o) {
        return o.id;
      });
      this.pendingPickerAction = action;
      this.showQuotePickerModal = true;
    },

    toggleSelectAllQuotesForPicker() {
      if (this.selectedQuotesForPicker.length === this.orders.length) {
        this.selectedQuotesForPicker = [];
      } else {
        this.selectedQuotesForPicker = this.orders.map(function (o) {
          return o.id;
        });
      }
    },

    // Dispatcher — branches on pendingPickerAction. We
    // call preventDefault on the bvModalEvent so the
    // modal stays open until our action finishes (or
    // errors out). Each handler is responsible for
    // closing the modal once it's done.
    confirmQuotePicker(bvModalEvent) {
      if (this.selectedQuotesForPicker.length === 0) {
        if (bvModalEvent) bvModalEvent.preventDefault();
        return;
      }
      if (bvModalEvent) bvModalEvent.preventDefault();

      if (this.pendingPickerAction === "saveForLater") {
        this.saveSelectedQuotesForLater();
      } else if (this.pendingPickerAction === "finalize") {
        this.finalizeSelectedQuotes();
      }
    },

    // ===== SAVE SELECTED QUOTES FOR LATER =====
    // Two paths:
    //
    // ALL SELECTED — the entire cart graduates to "saved
    // for later." Clear the active-quote pointers in
    // localStorage and navigate to /account/savedcarts.
    // Server state doesn't change in this branch (the cart
    // is already persisted; "saved for later" is just the
    // session-level concept of stepping away from it).
    //
    // PARTIAL — the user picked a subset to save. Those
    // selected POs split off into a NEW cart row server-side
    // (POST /cart/{id}/savepartial), and the user stays on
    // this page with the leftover quotes still in their
    // active cart. The new cart appears under SavedCarts so
    // they can resume the saved subset later.
    //
    // No quote ever gets deleted by this action — splitting
    // replaces the old "delete unselected" behavior.
    saveSelectedQuotesForLater() {
      if (this.savingQuote) return;
      this.savingQuote = true;

      var self = this;
      var selectedIds = this.selectedQuotesForPicker.slice();
      var allSelected = selectedIds.length === this.orders.length;

      // ===== ALL-SELECTED PATH =====
      // Ensure the cart on the server has Discriminator=Rfq
      // before navigating away. The cart may have been created
      // as Discriminator=Cart (if the user landed here via a
      // flow that created an Order cart first) — without this
      // the saved cart would appear under SavedOrders instead
      // of SavedQuotes in /account/savedcarts. convertToQuote
      // is idempotent so calling it on an already-Rfq cart
      // is harmless.
      if (allSelected) {
        apiService
          .convertToQuote({ cartId: self.cartIdRFQ })
          .then(function () {
            try {
              localStorage.cartIdRFQ = 0;
              delete localStorage.poNumberRFQ;
              delete localStorage.machineId;
              delete localStorage.assemblyId;

              if (self.companyId) {
                localStorage.removeItem("searchStaging_" + self.companyId);
              }
              localStorage.removeItem("searchStaging_anon");
            } catch (e) {
              // localStorage can throw in incognito / quota
              // contexts; ignore and proceed.
            }

            if (self.$cartBus) {
              self.$cartBus.$emit("cart-updated");
            }

            self.$bvToast.toast(
              "Your quote has been saved. You can resume it anytime from Saved Carts.",
              {
                title: "Quote Saved",
                autoHideDelay: 4000,
                variant: "success",
                solid: true,
              }
            );

            self.savingQuote = false;
            self.showQuotePickerModal = false;
            self.pendingPickerAction = null;
            // Pass flashCartId so SavedCarts.vue can briefly
            // highlight the just-saved row to confirm the save
            // landed. Using { path, query } (rather than a bare
            // path string) explicitly resets any stale query
            // params from the user's previous savedcarts visit
            // (e.g. ?tab=orders) so the user lands on the
            // default Saved tab where their freshly-saved quote
            // appears. cartIdRFQ at this point is the existing
            // cart id which has been flipped to Discriminator=
            // Rfq via convertToQuote above.
            self.$router.push({
              path: "/account/savedcarts",
              query: self.cartIdRFQ
                ? { flashCartId: self.cartIdRFQ }
                : {},
            });
          })
          .catch(function (err) {
            self.savingQuote = false;
            var traceId =
              err && err.error && err.error.response && err.error.response.data
                ? err.error.response.data.traceId
                : "";
            self.$bvToast.toast(
              "Could not save the quote. " +
                (traceId ? "TraceID: " + traceId : ""),
              {
                title: "Error",
                autoHideDelay: 5000,
                variant: "danger",
                solid: true,
              }
            );
          });
        return;
      }

      // ===== PARTIAL PATH =====
      // Split the SELECTED POs off into a new cart row via
      // the server's savepartial endpoint. The unselected
      // (leftover) quotes stay on the current cart so the
      // user can keep editing them.
      //
      // asQuote: true forces the new (split) cart's
      // Discriminator to Rfq regardless of the source cart's
      // discriminator. Without this, the new cart would
      // inherit the source's discriminator — which can be
      // Cart if the user landed here from an Order flow that
      // created the cart as Cart originally.
      apiService
        .savePartialQuotes({
          cartId: self.cartIdRFQ,
          purchaseOrderIds: selectedIds,
          asQuote: true,
        })
        .then(function (response) {
          // Refresh local state — splice the saved POs out
          // of this.orders so the page only shows the
          // leftover quotes. The cart row in localStorage
          // (cartIdRFQ) is unchanged; the user's still in
          // the same active session.
          self.orders = self.orders.filter(function (o) {
            return selectedIds.indexOf(o.id) === -1;
          });
          if (self.fullOrder && self.fullOrder.purchaseOrders) {
            self.fullOrder.purchaseOrders = self.fullOrder.purchaseOrders.filter(
              function (po) {
                return selectedIds.indexOf(po.id) === -1;
              }
            );
          }

          self.savingQuote = false;
          self.showQuotePickerModal = false;
          self.pendingPickerAction = null;

          if (self.$cartBus) {
            self.$cartBus.$emit("cart-updated");
          }

          var savedCount = selectedIds.length;
          self.$bvToast.toast(
            savedCount === 1
              ? "1 quote was saved. The remaining quotes stay in your active cart."
              : savedCount + " quotes were saved. The remaining quotes stay in your active cart.",
            {
              title: "Quote Saved",
              autoHideDelay: 4000,
              variant: "success",
              solid: true,
            }
          );

          // Navigate to /account/savedcarts so the user can
          // see their freshly-saved quote on the Saved tab.
          // Server returns the new cart's id in response.newCartId
          // (savepartial endpoint contract) — pass it as
          // flashCartId so SavedCarts highlights the row briefly.
          // Falls back to no flash if the response shape doesn't
          // include an id. Explicit { path, query } so any stale
          // ?tab= param from a previous savedcarts visit is
          // cleared, defaulting to the Saved tab.
          var newCartId = response && (response.newCartId || response.id)
            ? (response.newCartId || response.id)
            : null;
          self.$router.push({
            path: "/account/savedcarts",
            query: newCartId ? { flashCartId: newCartId } : {},
          });
        })
        .catch(function (err) {
          self.savingQuote = false;
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : "";
          self.$bvToast.toast(
            "Could not save the selected quotes. " +
              (traceId ? "TraceID: " + traceId : ""),
            {
              title: "Error",
              autoHideDelay: 5000,
              variant: "danger",
              solid: true,
            }
          );
        });
    },

    // ===== AUTO-REMOVE "JUST THIS TIME" SETUP ADDS =====
    // The cross-company reorder flow may have added machines/wear parts to a
    // company "just for this order/quote". When a quote is FINALIZED here, undo
    // those. Best-effort + fully isolated: the helper resolves on every path
    // and never throws, so it can't block the finalize success. Scoped strictly
    // to the finalized quote's company (this.companyId, fallback localStorage).
    runTempSetupCleanup(companyId) {
      var self = this;
      try {
        var cid =
          companyId || this.companyId || localStorage.companyId || "";
        cleanupTempSetupAdds(apiService, cid, function (msg, opts) {
          self.$bvToast.toast(msg, {
            title: (opts && opts.title) || "Temporary setup cleaned up",
            autoHideDelay: 8000,
            appendToast: true,
            variant: (opts && opts.variant) || "info",
            solid: true,
          });
        });
      } catch (e) {
        // Defensive only — cleanupTempSetupAdds (the helper) never throws.
      }
    },

    // ===== FINALIZE / PRINT SELECTED QUOTES =====
    // Mirrors the original saveQuote() flow, but routes
    // to the Saved Quote print page with a filtered
    // purchaseOrders list reflecting the user's
    // selection. The unselected quotes stay in the
    // active cart untouched — they remain editable on
    // /cart/rfqsaveditems after the print page closes.
    // ===== FINALIZE SELECTED QUOTES =====
    // Two paths, both end on SavedRFQ for the printable receipt
    // and persist a finalized (IsSubmitted=true, Discriminator=
    // Rfq) cart that surfaces under /account/savedcarts' Quotes
    // history tab afterwards.
    //
    // ALL SELECTED — entire cart graduates to a finalized
    // quote. Steps:
    //   1. convertToQuote — flip Discriminator to Rfq via raw
    //      SQL (idempotent if already Rfq).
    //   2. submitCart — set IsSubmitted=true so the row leaves
    //      the SavedCarts Saved tab and appears in the Quotes
    //      history tab.
    //   3. Navigate to /cart/savedrfq for the print receipt.
    //   4. Stash the finalized cart id in localStorage.cartIdRFQ
    //      so SavedRFQ can re-fetch it via getSuccessRFQ.
    //
    // PARTIAL — split selected POs into a NEW cart, finalize
    // that one, leave unselected on the active cart for further
    // editing. Steps:
    //   1. savePartialQuotes(asQuote: true) — server splits
    //      selected POs onto a brand-new cart row with
    //      Discriminator=Rfq.
    //   2. submitCart on the new cart id.
    //   3. Refresh local orders to drop the moved POs.
    //   4. Navigate to /cart/savedrfq for the new cart's
    //      receipt; localStorage.cartIdRFQ swapped to the
    //      finalized cart's id.
    finalizeSelectedQuotes() {
      if (!this.fullOrder || !this.cartIdRFQ) {
        this.cartError = true;
        this.showQuotePickerModal = false;
        this.pendingPickerAction = null;
        return;
      }
      if (this.savingQuote) return;
      this.savingQuote = true;

      var self = this;
      var selectedIds = this.selectedQuotesForPicker.slice();
      var allSelected = selectedIds.length === this.orders.length;

      this.showQuotePickerModal = false;
      this.pendingPickerAction = null;

      // ===== ALL-SELECTED PATH =====
      if (allSelected) {
        apiService
          .convertToQuote({ cartId: self.cartIdRFQ })
          .then(function () {
            return apiService.submitCart({ id: self.cartIdRFQ });
          })
          .then(function () {
            // Cart is now Discriminator=Rfq + IsSubmitted=true.
            // SavedRFQ.getSuccessRFQ reads from route params
            // (id, fullOrder, etc.) — pass the existing cart's
            // id and data so the receipt page renders without
            // a redundant server roundtrip.
            self.savingQuote = false;

            // Quote finalized → auto-remove any "just this time" setup adds.
            self.runTempSetupCleanup(self.companyId);

            if (self.$cartBus) {
              self.$cartBus.$emit("cart-updated");
            }

            // Stash finalized id so SavedRFQ + any back-nav
            // can recover state if route params get lost on
            // refresh.
            localStorage.cartIdRFQ = self.cartIdRFQ;

            self.$router.push({
              name: "Saved Quote",
              params: {
                id: self.cartIdRFQ,
                order: self.fullOrder,
                purchaseOrders: [],
                companyId: self.companyId,
                fullOrder: self.fullOrder,
                SpecialInstructions: self.SpecialInstructions,
                CreateQuote: true,
              },
            });
          })
          .catch(function (err) {
            self.savingQuote = false;
            var traceId =
              err && err.error && err.error.response && err.error.response.data
                ? err.error.response.data.traceId
                : "";
            self.$bvToast.toast(
              "Could not finalize the quote. " +
                (traceId ? "TraceID: " + traceId : ""),
              {
                title: "Error",
                autoHideDelay: 5000,
                variant: "danger",
                solid: true,
              }
            );
          });
        return;
      }

      // ===== PARTIAL PATH =====
      // Split selected POs to a new cart, finalize that, leave
      // unselected on the active cart.
      apiService
        .savePartialQuotes({
          cartId: self.cartIdRFQ,
          purchaseOrderIds: selectedIds,
          asQuote: true,
        })
        .then(function (response) {
          var newCartId = response && (response.newCartId || response.id);
          if (!newCartId) {
            return Promise.reject(
              new Error("Server didn't return a new cart id from savepartial.")
            );
          }
          // Submit the new cart so it surfaces in the Quotes
          // history tab. Pass newCartId through to the next
          // .then via Promise.resolve.
          return apiService
            .submitCart({ id: newCartId })
            .then(function () {
              return newCartId;
            });
        })
        .then(function (newCartId) {
          // Refresh local state — splice moved POs off this.orders
          // so the active cart on /cart/rfqsaveditems shows only
          // the leftover quotes when the user navigates back.
          self.orders = self.orders.filter(function (o) {
            return selectedIds.indexOf(o.id) === -1;
          });
          if (self.fullOrder && self.fullOrder.purchaseOrders) {
            self.fullOrder.purchaseOrders = self.fullOrder.purchaseOrders.filter(
              function (po) {
                return selectedIds.indexOf(po.id) === -1;
              }
            );
          }

          self.savingQuote = false;

          // Quote finalized (partial) → auto-remove any "just this time" adds.
          self.runTempSetupCleanup(self.companyId);

          if (self.$cartBus) {
            self.$cartBus.$emit("cart-updated");
          }

          // ===== PRESERVE ACTIVE CART POINTER =====
          // After a partial finalize, the ORIGINAL cart still
          // exists server-side with the unselected (leftover)
          // POs. SavedRFQ.orderSuccessful does a `delete
          // localStorage.cartIdRFQ` after rendering the
          // receipt — which would orphan the user's active
          // cart and force them to re-resume it from
          // SavedCarts.
          //
          // To avoid that, stash the original cart id under
          // a separate key (`pendingCartIdRFQ`) before the
          // navigation. /cart/rfqsaveditems' mounted hook
          // checks for this key and restores cartIdRFQ from
          // it on next visit, so the leftover quotes stay
          // visible in the active cart.
          //
          // localStorage.cartIdRFQ itself is set to the
          // NEW finalized cart id so SavedRFQ binds to and
          // renders the correct receipt.
          var originalCartId = self.cartIdRFQ;
          try {
            localStorage.pendingCartIdRFQ = String(originalCartId);
          } catch (e) {
            // localStorage can throw in incognito / quota
            // contexts; ignore and proceed — worst case
            // the user has to manually Resume.
          }
          localStorage.cartIdRFQ = newCartId;

          self.$router.push({
            name: "Saved Quote",
            params: {
              id: newCartId,
              order: null,
              purchaseOrders: [],
              companyId: self.companyId,
              fullOrder: null,
              SpecialInstructions: self.SpecialInstructions,
              CreateQuote: true,
            },
          });
        })
        .catch(function (err) {
          self.savingQuote = false;
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : "";
          self.$bvToast.toast(
            "Could not finalize the selected quotes. " +
              (traceId ? "TraceID: " + traceId : ""),
            {
              title: "Error",
              autoHideDelay: 5000,
              variant: "danger",
              solid: true,
            }
          );
        });
    },
    createOrder() {
      // "Convert to Order" entry point. We DON'T immediately fire the
      // convert API call — instead we pop a modal that lets the user
      // pick which of their saved quotes go into the new active cart.
      // The actual API call happens in confirmConvertToOrder() once
      // the user clicks the modal's confirm button.
      //
      // Why a modal:
      // A user with multiple saved quotes (one per machine/assembly)
      // may want to convert just one quote into an order while leaving
      // the others as quotes for later. The backend supports both modes
      // ('full' = flip the existing cart's discriminator; 'partial' =
      // create a new cart and re-parent the selected POs). The modal's
      // checkbox state determines which mode the API call uses.
      if (!this.fullOrder || !this.cartIdRFQ) {
        this.cartError = true;
        return;
      }
      if (!this.orders || this.orders.length === 0) {
        this.$bvToast.toast(
          "There are no quotes to convert.",
          {
            title: "Nothing to Convert",
            autoHideDelay: 4000,
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      // Pre-select all quotes — most common case is "convert everything"
      // and we want that to be a single click of the confirm button.
      this.selectedQuotesForConversion = this.orders.map(function (o) {
        return o.id;
      });
      this.showConvertModal = true;
    },

    toggleSelectAllQuotesForConversion() {
      if (this.selectedQuotesForConversion.length === this.orders.length) {
        this.selectedQuotesForConversion = [];
      } else {
        this.selectedQuotesForConversion = this.orders.map(function (o) {
          return o.id;
        });
      }
    },

    confirmConvertToOrder(bvModalEvent) {
      // bvModalEvent.preventDefault() lets us close the modal manually
      // only after the API call resolves. Without it the modal would
      // close immediately on click and the user would lose context if
      // the API errored out.
      if (this.selectedQuotesForConversion.length === 0) {
        if (bvModalEvent) bvModalEvent.preventDefault();
        return;
      }
      if (bvModalEvent) bvModalEvent.preventDefault();

      // We intentionally do NOT call saveRFQ() here. The quote is
      // already persisted server-side: any quantity edits in the
      // expanded view fire updateSubtotal → updatePurchaseOrder
      // immediately, and the cart's overall state was loaded fresh
      // by getCart() on mount. A redundant saveRFQ before the convert
      // call was failing for unrelated reasons (companyId routing in
      // the saveCart payload) and blocking the convert from running
      // at all.

      var self = this;
      var selectedIds = this.selectedQuotesForConversion.slice();
      var isFull = selectedIds.length === this.orders.length;

      // For full conversion we send an empty array so the backend takes
      // the cheap discriminator-flip path; otherwise we send the subset
      // and the backend creates a new cart + re-parents those POs.
      var poIdsForApi = isFull ? [] : selectedIds;

      // NOTE: This method (and its caller createOrder) is
      // currently dead code — no UI button is wired to
      // createOrder. Kept here in case the multi-quote
      // picker modal gets re-wired later. Same fix as
      // convertFromModify: use the proven rfqOrder path
      // instead of the missing convertQuoteToOrder. If true
      // partial convert is needed when this is rewired,
      // FeApiService.js will need a real partial-convert
      // wrapper added.
      apiService
        .rfqOrder({ id: this.cartIdRFQ })
        .then(function (response) {
          var newCartId = (response && response.id) || self.cartIdRFQ;

          localStorage.cartId = newCartId;
          // Always full convert here too — see comment above.
          try { delete localStorage.cartIdRFQ; } catch (e) {}

          self.showConvertModal = false;

          self.$router.push({
            name: "Cart",
            params: {
              cartId: newCartId,
              order: self.fullOrder,
              purchaseOrders: self.fullOrder
                ? self.fullOrder.purchaseOrders
                : [],
              companyId: self.companyId,
              fullOrder: self.fullOrder,
              SpecialInstructions: self.SpecialInstructions,
            },
          });
        })
        .catch(function (err) {
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : "";
          self.$bvToast.toast(
            "Could not convert quote to order. " +
              (traceId ? "TraceID: " + traceId : ""),
            {
              title: "Conversion Failed",
              autoHideDelay: 6000,
              variant: "danger",
              solid: true,
            }
          );
        });
    },
    addParts(order) {
      if (order.configuredWear == true) {
        this.$router.push({
          name: "Create Quote - Shop Wear Parts",
          params: {
            id: this.cartIdRFQ,
            order: order,
            number: order.number,
            machineId: order.machineId,
            assemblyId: order.assemblyId,
            configuredWear: order.configuredWear,
            partsList: order.parts,
            fullOrder: this.fullOrder,
          },
        });
      } else {
        this.$router.push({
          name: "Create Quote - Shop Assembly",
          params: {
            id: this.cartIdRFQ,
            order: order,
            number: order.number,
            machineId: order.machineId,
            assemblyId: order.assemblyId,
            configuredWear: order.configuredWear,
            partsList: order.parts,
            fullOrder: this.fullOrder,
          },
        });
      }
    },
    deletePO(order) {
      var idx = $.inArray(order, this.orders);
      this.orders.splice(idx, 1);

      apiService
        .deletePoNumber({
          id: order.id,
        })
        .then((request) => {
          if(this.orders.length>0){
            localStorage.poNumberRFQ = this.orders[this.orders.length - 1].number;
            this.poNumber = this.orders[this.orders.length - 1].number;
          }else{
            delete localStorage.poNumberRFQ;
            //0 cartIdRfq so it will hide clear cart on create quote screen
            localStorage.cartIdRFQ = 0;
            this.poNumber = null;
          }
          this.deletePOSuccessful(request);
          this.getTotal();
        })
        .catch((err) => this.deletePOFailed(err));
    },
    deletePOSuccessful() {},
    deletePOFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    changePoNumber(num) {
      localStorage.poNumberRFQ = num;

      this.editPoNumber = !this.editPoNumber;
      apiService.updatePONumbers({
          cartId: this.cartIdRFQ,
          poId: this.orders[this.orders.length - 1].id,
          newPoNumber: num,
        })
        // .updatePurchaseOrder({
        //   id: this.orders[this.orders.length - 1].id,
        //   Number: num,
        //   parts: this.orders[this.orders.length - 1].parts,
        // })
        .then(() => {
          this.orders[this.orders.length - 1].number = num;
        })
        .catch((err) => this.failed(err));
    },
  },
};
</script>

<style scoped>
/* ===== PAGE HEADER LAYOUT =====
   Mirrors the cart page's Review & Checkout header so users
   carry the same visual model between the cart and saved-quote
   flows. Title flexes wide, action buttons cluster on the right
   and wrap to a new line at narrow widths. */
.rfq-page-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.rfq-page-header h1 {
  flex: 1 1 auto;
  min-width: 200px;
  margin: 0;
  /* Flush-left so the title lines up with the action buttons below it (the
     cart page dropped its matching 48px nudge for the same reason). */
  padding-left: 0;
}

.rfq-header-actions {
  /* Full width of the header so the 5 actions spread HORIZONTALLY and wrap
     to ~2 rows. Previously `flex: 0 0 auto` sized this to its content's
     minimum (the widest button) when the header wrapped below the title in
     the narrow card, which forced one button per row (a vertical stack). */
  flex: 1 1 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 10px;
  flex-wrap: wrap;
}
.rfq-header-actions .btn {
  /* Size to the label (one line) and flow; wrapping handles the row breaks. */
  flex: 0 1 auto;
  white-space: nowrap;
}

/* ===== HEADER ACTION BUTTONS =====
   Three-button group: Back to Search (primary nav, red filled)
   / Save Quote (neutral white with dark border) / Clear Quotes
   (destructive white with red border). Class names match the
   cart page exactly so any future visual tweaks to those
   classes propagate to both pages from a single source. */

/* Back to Search — red filled, primary navigation. Arrow-left
   icon reinforces "go back to where you came from". */
.btn-back-search {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 18px;
  background: #c0392b;
  border: 1.5px solid #c0392b;
  border-radius: 6px;
  color: #fff !important;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.btn-back-search:hover,
.btn-back-search:focus {
  background: #a32e22;
  border-color: #a32e22;
  color: #fff !important;
  text-decoration: none;
  box-shadow: 0 2px 6px rgba(192, 57, 43, 0.25);
}

.btn-back-search .btn-icon {
  flex-shrink: 0;
  stroke-width: 2;
  color: inherit;
}

/* Save Quote — neutral white with dark border so it doesn't
   compete with the red Back-to-Search affordance. Inverts on
   hover for clear feedback. */
.btn-save-cart {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 18px;
  background: #fff;
  border: 1.5px solid #333;
  border-radius: 6px;
  color: #111 !important;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.btn-save-cart:hover,
.btn-save-cart:focus {
  background: #333;
  color: #fff !important;
  border-color: #333;
  text-decoration: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.btn-save-cart .btn-icon {
  flex-shrink: 0;
  stroke-width: 2;
  color: inherit;
}

/* Finalize / Print Quote — primary terminal action, so it
   gets the brand-red filled treatment to read as the
   "this is the action" of the page. Sits between the
   neutral Save for Later button and the destructive
   Clear Quotes. */
.btn-finalize-quote {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 18px;
  background: #c0392b;
  border: 1.5px solid #c0392b;
  border-radius: 6px;
  color: #fff !important;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: 0 1px 2px rgba(192, 57, 43, 0.15);
}

.btn-finalize-quote:hover,
.btn-finalize-quote:focus {
  background: #a93022;
  border-color: #a93022;
  color: #fff !important;
  text-decoration: none;
  box-shadow: 0 2px 6px rgba(192, 57, 43, 0.28);
}

.btn-finalize-quote .btn-icon {
  flex-shrink: 0;
  stroke-width: 2;
  color: inherit;
}

/* Clear Quotes — destructive variant. Same shape as the
   neutral button but with a red accent so it visually reads
   as "the cautious option" while staying secondary to the
   primary Back-to-Search affordance. */
.btn-clear-cart {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 18px;
  background: #fff;
  border: 1.5px solid #c0392b;
  border-radius: 6px;
  color: #c0392b !important;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.btn-clear-cart:hover,
.btn-clear-cart:focus {
  background: #c0392b;
  color: #fff !important;
  border-color: #c0392b;
  text-decoration: none;
  box-shadow: 0 2px 6px rgba(192, 57, 43, 0.18);
}

.btn-clear-cart .btn-icon {
  flex-shrink: 0;
  stroke-width: 2;
  color: inherit;
}

/* Narrow-screen stacking: header collapses to title above and
   action buttons below, with each button still visible in
   sequence. Matches the cart page's responsive treatment. */
@media (max-width: 575.98px) {
  .rfq-page-header {
    flex-direction: column;
    align-items: stretch;
  }
  .rfq-page-header h1 {
    padding-left: 0;
    text-align: center;
  }
  .rfq-header-actions {
    justify-content: center;
  }
}

/* ===== 2-COLUMN QUOTES GRID =====
   Renders quote cards side-by-side on desktop so the user
   sees more quotes at a glance without scrolling, matching
   the cart page's Order #1 / Order #2 layout. Stacks to a
   single column below lg where col-lg-6 would be too narrow
   for the inner content. */
.rfq-orders-grid-wrap {
  margin-top: 0;
}

.rfq-order-col {
  margin-bottom: 22px;
}

@media (max-width: 991.98px) {
  .rfq-order-col {
    margin-bottom: 16px;
  }
}

/* Lead-time note under the action button row. Subtle italic
   centered note clarifying delivery expectations. */
.rfq-leadtime-note {
  text-align: center;
  font-size: 12px;
  color: #888;
  font-style: italic;
  margin: 6px 0 0;
}

/* ===== QUOTE GROUP =====
   Outer card per quote. Mirrors the cart's .po-group: red
   left accent, light gray fill, soft shadow. The inner
   quote-assembly-card sits on top of this, white. */
.quote-group {
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-left: 3px solid #c0392b;
  border-radius: 8px;
  margin: 0;
  padding: 18px 20px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  height: 100%;
}

/* Quote header — sits above the assembly card. Sequential
   numbering ("Quote #1", "Quote #2", …) plus a small part
   count meta label. Matches the cart's order-header. */
.quote-header {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #e1e1e1;
}

.quote-header-label {
  font-size: 16px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
}

.quote-header-meta {
  font-size: 12px;
  color: #888;
  font-style: italic;
}

/* ===== QUOTE ASSEMBLY CARD =====
   White inner card holding the machine title + assembly +
   subtotal pill on top, then the parts content beneath. */
.quote-assembly-card {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 14px 16px;
}

.quote-assembly-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.quote-assembly-heading {
  flex: 1 1 auto;
  min-width: 0;
}

.quote-assembly-heading .assemblyName {
  font-size: 14px;
  font-weight: 700;
  color: #2a2a2a;
  letter-spacing: 0.3px;
  margin-bottom: 2px;
}

.quote-assembly-heading .assemblyDescription {
  font-size: 12px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Subtotal pill on the right side of the assembly header.
   Same gray-card treatment as the cart's PO subtotal. In
   narrow columns it stacks below the title via the
   .rfq-order-col override below. */
.quote-assembly-subtotal {
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 6px 12px;
}

.quote-subtotal-label {
  font-size: 11px;
  color: #555;
  white-space: normal;
  word-break: break-word;
}

.quote-subtotal-amount {
  font-size: 14px;
  font-weight: 700;
  color: #c0392b;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

/* In the 2-col grid the column is too narrow for the title +
   subtotal pill to sit side-by-side. Stack them. */
.rfq-order-col .quote-assembly-header {
  flex-direction: column;
  align-items: stretch;
}

.rfq-order-col .quote-assembly-subtotal {
  align-self: stretch;
  justify-content: space-between;
}

/* ===== COMPACT PARTS SUMMARY =====
   The default-visible pill row showing all parts at a
   glance. Click the + button to expand to the full
   editable b-table. */
.quote-parts-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid #ececec;
  border-radius: 4px;
}

.quote-expand-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #fff;
  border: 1.5px solid #c0392b;
  color: #c0392b;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.quote-expand-btn:hover {
  background: #c0392b;
  color: #fff;
}

.quote-parts-count {
  font-size: 11px;
  font-weight: 700;
  color: #555;
  letter-spacing: 0.4px;
}

.quote-parts-pills {
  flex: 1 1 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
}

.quote-part-pill {
  display: inline-block;
  padding: 2px 8px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  font-size: 11px;
  color: #444;
  white-space: nowrap;
}

.quote-parts-cost {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  margin-left: auto;
}

/* ===== EXPANDED PARTS DETAIL =====
   Reveals when the user clicks the + button. Shows the
   full b-table with editable qty + remove affordances. */
.quote-parts-detail {
  margin-top: 4px;
}

.quote-collapse-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #fff;
  border: 1px solid #c0c0c0;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  margin-bottom: 10px;
}

.quote-collapse-btn:hover {
  background: #c0392b;
  border-color: #c0392b;
  color: #fff;
}

/* ===== FOOTER ACTIONS =====
   Centered Add Parts / Delete buttons at the bottom of
   each quote card. Always visible regardless of expand
   state so users don't have to expand to add or delete. */
.quote-card-footer {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f1f1f1;
}

.quote-card-footer .btn-sm {
  font-size: 12px;
  padding: 7px 22px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-decoration: none;
}

/* ===== CONVERT-TO-ORDER MODAL =====
   Modal content styles for the popup that opens when the user
   clicks "Convert to Order" in the bottom action row. */
.convert-modal-intro {
  margin: 0 0 14px;
  font-size: 13px;
  color: #555;
}

.convert-quote-list {
  margin: 0 0 12px;
  padding: 0;
}

.convert-quote-row {
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid #ececec;
  border-radius: 6px;
  margin-bottom: 6px;
}

.convert-quote-row:last-child {
  margin-bottom: 0;
}

/* b-form-checkbox renders a wrapping <div class="custom-control">.
   We let the checkbox indicator stay default-positioned; the label
   gets our two-line layout. */
.convert-quote-row .custom-control-label {
  cursor: pointer;
  width: 100%;
}

.convert-quote-row-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 2px;
}

.convert-quote-row-label {
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
}

.convert-quote-row-meta {
  font-size: 12px;
  color: #444;
  font-weight: 600;
}

.convert-quote-row-sub {
  font-size: 11px;
  color: #888;
  letter-spacing: 0.2px;
  text-transform: uppercase;
}

.convert-quote-row-amount {
  color: #c0392b;
  font-weight: 700;
}

.convert-quote-row-poref {
  color: #555;
  font-weight: 500;
}

.convert-quote-row-poref strong {
  color: #222;
  font-weight: 700;
}

/* ===== GROUPED ROW (Quote # level) =====
   Heavier visual weight than the legacy per-assembly
   rows since each row now represents a whole Quote #
   group. Brand-red left border ties it to the on-page
   group treatment. .convert-quote-row-meta when used at
   group level reads "Quote Ref: <num>" inline with the
   label. Indeterminate state on the checkbox rendering
   is supplied by Bootstrap-Vue automatically. */
.convert-quote-row-grouped {
  border-left: 3px solid #c0392b;
}

.convert-quote-row-grouped .convert-quote-row-main {
  align-items: baseline;
}

.convert-quote-row-grouped .convert-quote-row-label {
  font-size: 14px;
  letter-spacing: 0.3px;
}

.convert-quote-row-grouped .convert-quote-row-meta {
  font-size: 12px;
  color: #555;
  font-weight: 400;
}

.convert-quote-row-grouped .convert-quote-row-meta strong {
  color: #222;
  font-weight: 700;
}

/* Per-assembly mini-list inside a grouped row. Renders
   each assembly's machine + name + part count on its own
   line so the user knows what they're checking. Smaller
   font and indented to read as supplementary detail
   beneath the group's primary label. */
.convert-quote-row-assemblies {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #ececec;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.convert-quote-row-assembly {
  font-size: 11px;
  color: #777;
  line-height: 1.4;
}

.convert-quote-toggle-all {
  text-align: right;
  margin: 0 0 12px;
}

.convert-quote-toggle-all a {
  font-size: 11px;
  font-weight: 700;
  color: #c0392b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
  text-decoration: none;
}

.convert-quote-toggle-all a:hover {
  text-decoration: underline;
}

.convert-quote-note {
  margin: 8px 0 0;
  font-size: 12px;
  color: #666;
  font-style: italic;
}

.convert-quote-note-warn {
  color: #c0392b;
  font-style: normal;
  font-weight: 600;
}

/* ===== MODIFY QUOTE MODAL =====
   Targeted via modal-class so styles reach the portaled
   modal root (scoped CSS doesn't apply to b-modal contents). */
.modify-quote-modal .modify-modal-intro {
  margin: 0 0 14px;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

/* ===== CONTEXT BANNER =====
   Sits above the modal-intro paragraph. Boxed, bordered,
   shadowed so the user's eye is drawn to it on modal
   open — this is the confirmation moment that ties their
   "I clicked X" action to "yes, here's what's happening
   next." Same visual treatment as Cart.vue's banner so
   the quote and order flows feel consistent.

   Color palette pulls a soft red tint from the brand
   accent so the banner reads as on-brand rather than
   a generic info panel. */
.modify-quote-modal .modify-restored-banner,
.quote-picker-modal .modify-restored-banner {
  background: linear-gradient(
    135deg,
    #fff5f3 0%,
    #fff 100%
  );
  border: 2px solid #c0392b;
  border-radius: 10px;
  padding: 18px 22px;
  margin: 0 0 18px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(192, 57, 43, 0.12),
              0 1px 3px rgba(0, 0, 0, 0.04);
}

.modify-quote-modal .modify-restored-title,
.quote-picker-modal .modify-restored-title {
  font-size: 20px;
  font-weight: 800;
  color: #c0392b;
  letter-spacing: 0.3px;
  margin-bottom: 6px;
  line-height: 1.2;
}

.modify-quote-modal .modify-restored-body,
.quote-picker-modal .modify-restored-body {
  font-size: 13px;
  color: #444;
  line-height: 1.5;
}

.modify-quote-modal .modify-restored-body strong,
.quote-picker-modal .modify-restored-body strong {
  color: #222;
  font-weight: 700;
}

@media (max-width: 575.98px) {
  .modify-quote-modal .modify-restored-title,
  .quote-picker-modal .modify-restored-title {
    font-size: 18px;
  }
  .modify-quote-modal .modify-restored-banner,
  .quote-picker-modal .modify-restored-banner {
    padding: 14px 16px;
  }
}

.modify-quote-modal .modify-options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modify-quote-modal .modify-option {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #e3e3e3;
  border-left: 3px solid #c0392b;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
}

.modify-quote-modal .modify-option:hover {
  background: #fff8f8;
  border-color: #c0392b;
  box-shadow: 0 2px 6px rgba(192, 57, 43, 0.12);
  transform: translateX(2px);
}

.modify-quote-modal .modify-option-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.modify-quote-modal .modify-option-orderlabel {
  font-weight: 700;
  color: #c0392b;
  font-size: 14px;
}

.modify-quote-modal .modify-option-assembly {
  font-weight: 600;
  color: #222;
  font-size: 13px;
}

.modify-quote-modal .modify-option-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
  color: #666;
}

.modify-quote-modal .modify-option-meta strong {
  color: #444;
}

/* ===== CREATE NEW QUOTE BUTTON =====
   Subtle outline variant — sits next to Modify/Save/
   Finalize/Clear in the top header without competing
   with the brand-red primary actions. */
.btn-create-new {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 18px;
  background: #fff;
  border: 1.5px solid #888;
  border-radius: 6px;
  color: #333 !important;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.btn-create-new:hover,
.btn-create-new:focus {
  background: #f5f5f5;
  border-color: #555;
  color: #111 !important;
  text-decoration: none;
}
.btn-create-new .btn-icon {
  flex-shrink: 0;
}

/* ===== MODIFY / CONVERT QUOTE MODAL — EDITOR =====
   The modal's body has three regions: the picker (radio
   rows of quotes), the editor (PO + Special Instructions
   + apply-to-all), and the action row (Save / Add /
   Convert). All three sit on the same modal-class so
   they share the brand red accent on selected rows. */
.modify-quote-modal .modify-quote-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 18px;
  /* No inner scroll — every group renders fully so the
     user can see all assemblies without an internal
     scrollbar. The modal's natural body scroll handles
     overflow if there are many groups. */
}

/* ===== ORDER / QUOTE GROUP =====
   One container per Quote # holding a clickable header row,
   a chevron toggle, and (when expanded) the assembly rows
   with their parts lists. Collapsed by default so the user
   sees an overview of all groups first. Mirrors Cart's
   .modify-order-group treatment so the two modals feel
   visually consistent. */
.modify-quote-modal .modify-order-group {
  background: #fafafa;
  border: 1px solid #ececec;
  border-left: 3px solid #c0392b;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 12px;
  transition: box-shadow 200ms ease;
}

.modify-quote-modal .modify-order-group.is-expanded {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.modify-quote-modal .modify-order-group-head-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modify-quote-modal .modify-order-group-head {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
}

.modify-quote-modal .modify-order-group-head:hover
  .modify-order-group-label {
  color: #a52d20;
}

.modify-quote-modal .modify-order-group-label {
  flex: 1 1 auto;
  font-weight: 700;
  color: #c0392b;
  font-size: 14px;
  letter-spacing: 0.3px;
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
}

.modify-quote-modal .modify-order-group-po {
  font-size: 12px;
  font-weight: 400;
  color: #555;
  letter-spacing: 0;
}

.modify-quote-modal .modify-order-group-po strong {
  color: #222;
  font-weight: 700;
}

.modify-quote-modal .modify-order-group-total {
  flex: 0 0 auto;
  font-weight: 700;
  font-size: 14px;
  color: #222;
  font-variant-numeric: tabular-nums;
  text-align: center;
  white-space: nowrap;
}

.modify-quote-modal .modify-order-group-count {
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #888;
  background: #eee;
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
}

/* ===== EXPAND TOGGLE ROW =====
   Sits beneath the title. Subdued so the header row above
   stays primary. Chevron icon flips between '+' / '−'. */
.modify-quote-modal .modify-order-group-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 0;
  padding: 6px 0 0;
  margin: 0;
  cursor: pointer;
  color: #777;
  font-size: 12px;
  font-weight: 500;
  transition: color 180ms ease;
}

.modify-quote-modal .modify-order-group-toggle:hover {
  color: #c0392b;
}

.modify-quote-modal .modify-order-group-toggle-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #d8d8d8;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  color: #555;
  transition: background 180ms ease, border-color 180ms ease,
              color 180ms ease;
}

.modify-quote-modal .modify-order-group-toggle:hover
  .modify-order-group-toggle-icon,
.modify-quote-modal .modify-order-group-toggle.is-open
  .modify-order-group-toggle-icon {
  background: #c0392b;
  border-color: #c0392b;
  color: #fff;
}

.modify-quote-modal .modify-order-group-toggle-text {
  letter-spacing: 0.2px;
}

/* ===== ASSEMBLIES CONTAINER =====
   Renders only when expanded. Top margin + light border
   separates from the header above. */
.modify-quote-modal .modify-order-group-assemblies {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e3e3e3;
}

/* ===== ASSEMBLY PICK WRAPPER =====
   Outer container that holds both the radio row AND the
   parts list below. Single border rounds the whole stack
   so radio + parts read as one unit. */
.modify-quote-modal .modify-quote-pick-wrap {
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  background: #fff;
  margin-bottom: 8px;
  transition: border-color 200ms ease, box-shadow 200ms ease;
}

.modify-quote-modal .modify-quote-pick-wrap:last-child {
  margin-bottom: 0;
}

/* Pick row inside the wrap loses its own border since the
   wrap carries it now — otherwise we'd get double borders. */
.modify-quote-modal .modify-quote-pick-wrap .modify-quote-pick-row {
  border: 0;
  border-radius: 6px 6px 0 0;
  margin-bottom: 0;
  background: transparent;
}

.modify-quote-modal .modify-quote-pick-wrap .modify-quote-pick-row.is-selected {
  background: #fff8f8;
  box-shadow: none;
}

.modify-quote-modal .modify-option-cost {
  flex: 0 0 auto;
  font-weight: 700;
  font-size: 13px;
  color: #222;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  margin-left: auto;
  padding-left: 12px;
}

/* ===== PARTS LIST =====
   Lives inside each .modify-quote-pick-wrap, below the
   assembly's radio row. Lists each part with code · desc ·
   qty · cost · X delete. */
.modify-quote-modal .modify-parts-list {
  border-top: 1px dashed #ececec;
  padding: 6px 12px 8px;
  background: #fafafa;
  border-radius: 0 0 6px 6px;
}

.modify-quote-modal .modify-part-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  font-size: 12px;
  border-bottom: 1px dotted #eee;
}

.modify-quote-modal .modify-part-row:last-child {
  border-bottom: 0;
}

.modify-quote-modal .modify-part-info {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.modify-quote-modal .modify-part-discount-chip {
  display: inline-block;
  background: #c8e6c9;
  color: #1b5e20;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 6px;
  vertical-align: middle;
  white-space: nowrap;
  /* Parent .modify-part-info is flex-direction: column.
     Flex items default to align-items: stretch on the
     cross axis, which was stretching this chip to fill
     the full row width. Pin to the start so it sizes
     to content. */
  align-self: flex-start;
}

/* Main-page Review (outside the Modify modal) part-detail
   rows. Same chip visual but parent .part-detail-meta is a
   flex ROW — chips size to content naturally with no
   align-self workaround needed. */
.part-discount-chip {
  display: inline-block;
  background: #c8e6c9;
  color: #1b5e20;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 6px;
  vertical-align: middle;
  white-space: nowrap;
}
.modify-quote-modal .modify-part-code {
  font-family: monospace;
  font-weight: 700;
  color: #222;
  font-size: 11px;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modify-quote-modal .modify-part-desc {
  color: #666;
  font-size: 11px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modify-quote-modal .modify-part-qty {
  flex: 0 0 auto;
  font-weight: 600;
  color: #555;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.modify-quote-modal .modify-part-cost {
  flex: 0 0 auto;
  font-weight: 700;
  color: #222;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  min-width: 70px;
  text-align: right;
}

.modify-quote-modal .modify-part-remove {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid transparent;
  background: transparent;
  color: #c0392b;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background 180ms ease, border-color 180ms ease,
              color 180ms ease;
}

.modify-quote-modal .modify-part-remove:hover {
  background: #c0392b;
  border-color: #c0392b;
  color: #fff;
}

.modify-quote-modal .modify-quote-pick-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 12px;
  border: 1px solid #e3e3e3;
  border-left: 3px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  margin: 0;
}

.modify-quote-modal .modify-quote-pick-row:hover {
  background: #fafafa;
}

.modify-quote-modal .modify-quote-pick-row.is-selected {
  background: #fff8f8;
  border-color: #c0392b;
  box-shadow: 0 1px 4px rgba(192, 57, 43, 0.10);
}

.modify-quote-modal .modify-quote-pick-radio {
  margin-top: 4px;
  flex-shrink: 0;
}

.modify-quote-modal .modify-quote-pick-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.modify-quote-modal .modify-editor {
  margin-top: 6px;
  padding: 14px 16px;
  background: #fafafa;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
}

.modify-quote-modal .modify-editor-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.modify-quote-modal .modify-editor-row:last-child {
  margin-bottom: 0;
}

.modify-quote-modal .modify-editor-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #555;
  margin: 0;
}

.modify-quote-modal .modify-editor-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
}

.modify-quote-modal .modify-editor-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}

.modify-quote-modal .modify-editor-textarea {
  resize: vertical;
  min-height: 70px;
  font-family: inherit;
}

.modify-quote-modal .modify-editor-toggle-row {
  margin-top: 4px;
}

.modify-quote-modal .modify-apply-all {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 13px;
  color: #444;
  cursor: pointer;
}

.modify-quote-modal .modify-apply-all input[type="checkbox"] {
  margin: 0;
  flex-shrink: 0;
}

.modify-quote-modal .modify-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
  flex-wrap: wrap;
}

.modify-quote-modal .modify-action-btn {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 12px;
  padding: 8px 16px;
}

/* ===== PER-QUOTE SPECIAL INSTRUCTIONS DISPLAY =====
   Renders below the parts list inside an expanded quote
   card. Read-only — edits happen via the Modify / Convert
   Quote modal. Only mounts when the order actually has SI
   text so empty quotes don't show a blank box. Slight
   indent + amber accent reads as "extra context" without
   competing with the parts table. */
.quote-special-instructions {
  margin: 12px 16px 0;
  padding: 10px 14px;
  background: #fff8e1;
  border: 1px solid #e7d6a6;
  border-left: 3px solid #c0392b;
  border-radius: 4px;
}

.quote-special-instructions-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #6b5614;
  margin-bottom: 4px;
}

.quote-special-instructions-text {
  font-size: 13px;
  color: #333;
  line-height: 1.45;
  white-space: pre-wrap;
}
</style>

<!-- Unscoped: targets BootstrapVue's own .modal-dialog / .modal-content
     wrapper elements, which the scoped block above cannot reach. -->
<style>
/* "Modify / Convert Quote" modal — push the dialog below the fixed site
   header so the title and "Pick a quote to edit…" intro aren't clipped
   under the nav, and let the body scroll internally when tall. */
.modify-quote-modal .modal-dialog {
  margin-top: 170px;
  margin-bottom: 30px;
}
.modify-quote-modal .modal-content {
  max-height: calc(100vh - 200px);
}
.modify-quote-modal .modal-body {
  overflow-y: auto;
}
</style>